import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import TableRowsIcon from "@mui/icons-material/TableRows";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Container,
  Divider,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { groupBy, uniq } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Custom3dotString from "../../../components/Custom3dotString";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import api from "../../../redux/actions/api";
import {
  checkedCallInApi,
  checkedInApi,
  getAppointmentsByClinicAndFilter,
  getAppointmentsWithFilter,
  getClinicFacilities,
  getClinicHolidays,
  getClinicOpeningHours,
  getClinicVetsUnavailability,
  updateAppointmentStatusById,
  updateCancel,
  updateCompleted,
  updateFinalize,
} from "../../../redux/reducers/clinicSlice";
import { deleteVetHolidayById } from "../../../redux/reducers/doctorSlice";
import { getMedicalHistoryByPetId } from "../../../redux/reducers/medicalHistorySlice";
import { storeApntmntScreenDetails } from "../../../redux/reducers/mixedSlice";
import { AppColors } from "../../../util/AppColors";
import {
  appointmentStatusList,
  bookingTypes,
  facilityTypes,
  homeVisitStatusList,
  physicalStatusList,
  serviceTypes,
  typeList,
  virtualStatusList,
} from "../../../util/dropList";
import { appointmentTypeEnum } from "../../../util/enum";
import {
  createTimeSlots,
  getCardColorByApptStatus,
  getClassNameForCalendar,
  getDateList,
  getDatesInRange,
  getListWithAll,
  getRightSaluation,
  getSlotTimeRangeKey,
  getTypeByAppointmentType,
  getWeekRange,
  isTimeInSlot,
} from "../../../util/function";
import { tblGrayBack } from "../../../util/object";
import { superTailsClinicId } from "../../../util/server";
import AdmitPatient from "../../CommonScreens/AdmitPatient/AdmitPatient";
import BookAnAppointment from "../../CommonScreens/BookAppointmentment/BookAnAppointment";
import VetBookAnAppointment from "../../CommonScreens/BookAppointmentment/VetBookAnAppointment";
import UpdatePetWeight from "../../CommonScreens/UpdatePetWeight/UpdatePetWeight";
import MedicalHistoryView from "../../Hospital/ClinicPetDetails/MedicalHistory/MedicalHistoryView";
// import MedicalHistoryView from "../ClinicPetDetails/MedicalHistory/MedicalHistoryView";

const PERSPECTIVE_VET = "vet";
const PERSPECTIVE_FACILITY = "facility";
const LAYOUT_GRID = "grid";
const LAYOUT_LIST = "list";

const CAL_TIME_COL_WIDTH = 100;
const CAL_VET_COL_MIN_WIDTH = 220;
const CAL_WEEK_VET_HEADER_HEIGHT = 60;
const CAL_SLOT_ROW_HEIGHT = 90;
const CAL_MAX_ROW_VISIBLE_APNT_CARDS = 2;
const CAL_MAX_ROW_VISIBLE_APNT_CARDS_FEW_VETS = 3;
const CAL_FEW_VETS_THRESHOLD = 2;
const CAL_MULTI_CARD_MAX_VETS = 3;

const getCalWeekHeaderCellStyle = (columnIndex) => ({
  ...(columnIndex === 0
    ? {
        backgroundColor: AppColors.white,
        color: AppColors.black,
        textAlign: "left",
        position: "sticky",
        left: 0,
        zIndex: 105,
      }
    : {
        backgroundColor: AppColors.white,
        color: AppColors.black,
        textAlign: "center",
      }),
  border: ".5px solid #e5e7eb",
  height: CAL_WEEK_VET_HEADER_HEIGHT,
  maxHeight: CAL_WEEK_VET_HEADER_HEIGHT,
  padding: "4px 8px",
  boxSizing: "border-box",
  overflow: "hidden",
  verticalAlign: "middle",
  ...getCalColWidthStyle(columnIndex),
});

const getCalTimeColStyle = () => ({
  width: CAL_TIME_COL_WIDTH,
  minWidth: CAL_TIME_COL_WIDTH,
  maxWidth: CAL_TIME_COL_WIDTH,
  boxSizing: "border-box",
});

const getCalVetColStyle = () => ({
  width: "auto",
  minWidth: CAL_VET_COL_MIN_WIDTH,
});

const getCalTableMinWidth = (vetCount) =>
  CAL_TIME_COL_WIDTH + vetCount * CAL_VET_COL_MIN_WIDTH;

const getMaxVisibleApntCards = (totalCount, vetCount) => {
  if (totalCount <= 1) return totalCount;
  const maxVisible =
    vetCount <= CAL_FEW_VETS_THRESHOLD
      ? CAL_MAX_ROW_VISIBLE_APNT_CARDS_FEW_VETS
      : CAL_MAX_ROW_VISIBLE_APNT_CARDS;
  return Math.min(totalCount, maxVisible);
};

const parseTimeToMinutes = (time) => {
  if (!time) return null;
  const normalized = String(time).slice(0, 5);
  const [hour, minute] = normalized.split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
};

const getUnavailabilitySlotSize = (startTime, endTime, fallback = 15) => {
  const startMins =
    startTime instanceof Date
      ? startTime.getHours() * 60 + startTime.getMinutes()
      : parseTimeToMinutes(startTime);
  const endMins =
    endTime instanceof Date
      ? endTime.getHours() * 60 + endTime.getMinutes()
      : parseTimeToMinutes(endTime);
  if (startMins == null || endMins == null || endMins <= startMins) {
    return fallback;
  }
  return endMins - startMins;
};

const getCalendarSlotRangeMinutes = (slotStart, slotSize = 60) => {
  const startMins = parseTimeToMinutes(slotStart);
  if (startMins == null) return null;
  const [startHour, startMinute] = slotStart.split(":").map(Number);
  const slotsPerHour = 60 / slotSize;
  const slotIndex = startMinute / slotSize;
  const isLastSlotInHour = slotIndex === slotsPerHour - 1;

  const endMins =
    slotSize === 60 || isLastSlotInHour
      ? (startHour + 1) * 60
      : startMins + slotSize;

  return { startMins, endMins };
};

const doesSlotOverlapUnavailability = (slotStart, slotSize, unavailability) => {
  const range = getCalendarSlotRangeMinutes(slotStart, slotSize);
  if (!range) return false;
  const unavailStart = parseTimeToMinutes(unavailability?.startTime);
  const unavailEnd = parseTimeToMinutes(unavailability?.endTime);
  if (unavailStart == null || unavailEnd == null) return false;
  return range.startMins < unavailEnd && range.endMins > unavailStart;
};

const isFirstUnavailabilityDisplaySlot = (
  slotStart,
  slotSize,
  unavailability,
  timeSlots = [],
) => {
  if (!doesSlotOverlapUnavailability(slotStart, slotSize, unavailability)) {
    return false;
  }

  const range = getCalendarSlotRangeMinutes(slotStart, slotSize);
  const unavailStart = parseTimeToMinutes(unavailability?.startTime);

  if (range && unavailStart != null) {
    if (unavailStart >= range.startMins && unavailStart < range.endMins) {
      return true;
    }
  }

  const firstOverlappingSlot = (Array.isArray(timeSlots) ? timeSlots : []).find(
    (slot) => doesSlotOverlapUnavailability(slot, slotSize, unavailability),
  );

  return firstOverlappingSlot === slotStart;
};

const getOverlappingUnavailabilitySlots = (
  slotSize,
  unavailability,
  timeSlots = [],
) =>
  (Array.isArray(timeSlots) ? timeSlots : []).filter((slot) =>
    doesSlotOverlapUnavailability(slot, slotSize, unavailability),
  );

const getUnavailabilitySlotBlockClass = (
  slotStart,
  slotSize,
  unavailability,
  timeSlots = [],
) => {
  const overlappingSlots = getOverlappingUnavailabilitySlots(
    slotSize,
    unavailability,
    timeSlots,
  );
  if (!overlappingSlots.includes(slotStart)) return "";

  if (overlappingSlots.length === 1) return "new-cal-slot-unavailable-single";
  if (overlappingSlots[0] === slotStart)
    return "new-cal-slot-unavailable-start";
  if (overlappingSlots[overlappingSlots.length - 1] === slotStart) {
    return "new-cal-slot-unavailable-end";
  }
  return "new-cal-slot-unavailable-middle";
};

const getApntEndTimeRaw = (apnt) => {
  if (!apnt || apnt?.immediate) return null;
  const raw = apnt?.appointmentEndTime ?? apnt?.endTime ?? null;
  if (!raw || raw === "Invalid date") return null;
  const parsed = moment(raw, ["HH:mm", "H:mm"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : null;
};

const doesSlotOverlapAppointmentTime = (
  slotStart,
  slotSize,
  appointmentStart,
  appointmentEnd,
) => {
  const range = getCalendarSlotRangeMinutes(slotStart, slotSize);
  if (!range || !appointmentStart || !appointmentEnd) return false;

  const apntStart = parseTimeToMinutes(appointmentStart);
  const apntEnd = parseTimeToMinutes(appointmentEnd);
  if (apntStart == null || apntEnd == null || apntEnd <= apntStart)
    return false;

  return range.startMins < apntEnd && range.endMins > apntStart;
};

const getOverlappingAppointmentSlots = (
  slotSize,
  appointmentStart,
  appointmentEnd,
  timeSlots = [],
) =>
  (Array.isArray(timeSlots) ? timeSlots : []).filter((slot) =>
    doesSlotOverlapAppointmentTime(
      slot,
      slotSize,
      appointmentStart,
      appointmentEnd,
    ),
  );

const getAppointmentSlotBlockClass = (
  slotStart,
  slotSize,
  appointmentStart,
  appointmentEnd,
  timeSlots = [],
) => {
  const overlappingSlots = getOverlappingAppointmentSlots(
    slotSize,
    appointmentStart,
    appointmentEnd,
    timeSlots,
  );
  if (!overlappingSlots.includes(slotStart)) return "";

  if (overlappingSlots.length === 1) return "new-cal-slot-apnt-block-single";
  if (overlappingSlots[0] === slotStart) return "new-cal-slot-apnt-block-start";
  if (overlappingSlots[overlappingSlots.length - 1] === slotStart) {
    return "new-cal-slot-apnt-block-end";
  }
  return "new-cal-slot-apnt-block-middle";
};

const getCalColWidthStyle = (columnIndex) =>
  columnIndex === 0 ? getCalTimeColStyle() : getCalVetColStyle();

const getCalColInnerStyle = (columnIndex) =>
  columnIndex === 0
    ? { width: CAL_TIME_COL_WIDTH, maxWidth: CAL_TIME_COL_WIDTH }
    : { width: "100%" };

const renderCalColGroup = (calendarVetList) => (
  <colgroup>
    <col
      className="new-cal-time-col-col"
      style={{ width: CAL_TIME_COL_WIDTH }}
    />
    {calendarVetList?.map((vet, idx) => (
      <col
        key={`cal-col-${vet?.doctorId ?? vet?.value ?? idx}`}
        className="new-cal-vet-col-col"
        style={{ minWidth: CAL_VET_COL_MIN_WIDTH }}
      />
    ))}
  </colgroup>
);

const SUMMARY_STATS = {
  todayTotal: 24,
  todayCompleted: 18,
  pendingRequests: 5,
  onDuty: 6,
  onLeave: 2,
};

const CalSummaryCard = ({
  icon,
  iconBgClass,
  title,
  value,
  footer,
  progressPercent,
}) => (
  <div className="w32Per">
    <div className="back-white br8brblack p5 flex-row-ali-cen">
      <div className={`imghw40 flex-center br10 mh10 ${iconBgClass}`}>
        {icon}
      </div>
      <div className="flex1">
        <Typography className="fs10 font-medium gray-color">{title}</Typography>
        <Typography className="fs16 font-bold black">{value}</Typography>
        {footer ? (
          <div className="flex-row-ali-cen">
            <Typography className="fs10 font-medium gray-color">
              {footer}
            </Typography>
            {progressPercent != null ? (
              <div
                className="gray-back2 br5 ml10"
                style={{ height: 6, flex: 1, maxWidth: 72, overflow: "hidden" }}
              >
                <div
                  className="green-back6 br5"
                  style={{
                    height: "100%",
                    width: `${progressPercent}%`,
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <Typography className="fs10 font-medium white-color">1</Typography>
        )}
      </div>
    </div>
  </div>
);

const CalToggleBtn = ({ active, onClick, children }) => (
  <Typography
    component="div"
    className={`font-bold fs14 cursor flex-center ${
      active ? "blue-back-con2" : "day-type-box2 black"
    }`}
    onClick={onClick}
  >
    {children}
  </Typography>
);

const tblHdrs = [
  "petName",
  "appointmentType",
  "serviceType",
  "vetName",
  "apntDate",
  "status",
  "action",
];
const nameExpan = {
  petName: "Pet Name",
  appointmentType: "Consultation Type",
  serviceType: "Service Type",
  vetName: "Vet Name",
  apntDate: "Appt Date/Time",
  status: "Status",
  action: "",
};
const admitReqList = ["remark", "vet"];
const allObj = { name: "All", value: "all" };
const initCons = ["Physical", "Virtual", "Home"];
const cardSty = {
  borderRadius: 1,
  padding: 2,
  borderTopColor: AppColors.appPrimary,
};
const initTimeObj = { times: [], ids: [] };
const initSearch = {
  petName: "",
  selectedVet: [allObj],
  consultationType: [],
  status: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Type",
  "Customer Name",
  "Appt Date",
  "Appt Time",
  "Service Type",
];

const statusMap = {
  Upcoming: "scheduled",
  "Checked-in": "inprogress",
  "In Transit": "intransit",
  Consultation: "consultation",
  Billing: "finalize",
  Completed: "completed",
  Cancelled: "cancelled",
};

const initCalFilter = {
  visbile: false,
  settingVisible: true,
  showStats: false,
  showAvailability: false,
  slotSize: 60,
  showPhysical: true,
  showVirtual: true,
  showHomeVisit: true,
  serviceType: [{ name: "All", value: "All" }],
  roles: "Vets",
  vets: [{ name: "All", value: "All" }],
  appointmentStatus: [{ name: "All", value: "All" }],
  facilityTypes: [{ name: "All", value: "All" }],
};
const defObj = { clinic: {}, vets: [], dataSharing: {}, facilities: [] };

const NewCalendar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timeRef = useRef(null);
  const moreTriggerRef = useRef(null);
  const apntMenuTriggerRef = useRef(null);
  const calendarToolbarRef = useRef(null);
  const calendarTableAnchorRef = useRef(null);
  const fetchRef = useRef(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [petData, setPetData] = useState(null);
  const [vetDropList, setVetDropList] = useState([allObj]);
  const [selectedVet, setSelectedVet] = useState(allObj);
  const [selectedType, setSelectedType] = useState("date");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekDate, setWeekDate] = useState(new Date());
  const [timeList, setTimeList] = useState([]);
  const [dateList, setDateList] = useState([]);
  const [conTypes, setConTypes] = useState(initCons);
  const [reqApnts, setReqApnts] = useState(null);
  const [sleServiceTypes, setSelServiceTypes] = useState(["All"]);
  const [anchorEls, setAnchorEls] = useState({});
  const [expandedMoreSlot, setExpandedMoreSlot] = useState(null);
  const [morePopupAnchor, setMorePopupAnchor] = useState(null);
  const [openMenuPortal, setOpenMenuPortal] = useState(null);
  const [slctedApnt, setSlctedApnt] = useState(null);
  const [reSchReAssModVsble, setReSchReAssModVsble] = useState(false);
  const [canModVsble, setCanModVsble] = useState(false);
  const [noShowModVsble, setNoShowModVsble] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [admitModVsble, setAdmitModVsble] = useState(false);
  const [modValues, setModValues] = useState({});
  const [modErrs, setModErrs] = useState({});
  const [selectTime, setSelectTime] = useState(null);
  const [timeObject, setTimeObject] = useState(initTimeObj);
  const [flowModVsble, setFlowModVsble] = useState(false);
  const [updateWeightVsble, setUpdateWeightVsble] = useState(false);
  const [apntBookModVsble, setApntBookModVsble] = useState(false);
  const [unavailEditModVsble, setUnavailEditModVsble] = useState(false);
  const [unavailEditData, setUnavailEditData] = useState(null);
  const [unavailDeleteModVsble, setUnavailDeleteModVsble] = useState(false);
  const [selectedUnavailability, setSelectedUnavailability] = useState(null);
  const [openUnavailMenuPortal, setOpenUnavailMenuPortal] = useState(null);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [srchValue, setSrchValue] = useState("");
  const [page, setPage] = useState(1);
  const itemSize = 10;
  const [assignVetModVisible, setAssignVetModVisible] = useState(false);
  const [assignVetvalues, setAssignVetValues] = useState({});
  const [slctedApntStatus, setSlctedApntStatus] = useState({
    name: "All",
    value: "All",
  });
  const [filterValues, setFilterValues] = useState(initFilter);
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [perspective, setPerspective] = useState(PERSPECTIVE_VET);
  const [layoutMode, setLayoutMode] = useState(LAYOUT_GRID);
  const [calFilterValues, setCalFilterValues] = useState(initCalFilter);
  const [tempFilterValues, setTempFilterValues] = useState(initCalFilter);
  const [calendarTableHeight, setCalendarTableHeight] = useState(null);
  const {
    appointments,
    vetAppointments,
    appointmentsWithFilter,
    appointmentsByFilter,
    facilities,
    appointmentsForSuggestion,
    selectedClinicBranch,
    clinicVetsUnavailability,
    holidays,
    openingHours,
  } = useSelector((state) => state?.clinic);
  const [vetList, setVetList] = useState(appointments?.doctors);
  const clinicVetList = useSelector((state) => state?.vet?.vetList?.vets);
  // const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const appointmentLists = appointments?.data;
  const user = localStorage.getItem("user");
  const role = localStorage.getItem("role");
  const profile = JSON.parse(user);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    clinic: clinicDetails,
    vets: vetsList,
    dataSharing,
    // facilities,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        dataSharing: reqData?.dataSharing ?? {},
        facilities: Array.isArray(reqData?.facilities)
          ? reqData?.facilities
          : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);
  const clinicVets = vetsList;
  // role === "ROLE_DOCTOR" && selectedClinicBranch?.branchId
  //   ? (clinicVetList ?? [])
  //   : (vetsList ?? []);

  const weekRange = useMemo(() => getWeekRange(weekAnchor), [weekAnchor]);

  const dateLabel = useMemo(() => {
    if (selectedType === "week") {
      return `${moment(weekRange.startDate).format("D MMM")} – ${moment(
        weekRange.endDate,
      ).format("D MMM YYYY")}`;
    }
    return moment(selectedDate).format("ddd, DD MMM YYYY");
  }, [selectedType, selectedDate, weekRange]);

  useEffect(() => {
    onInitApiCalls();
    getDateAndTimeLists();
    setFilterValues({ ...initFilter, selectedList: [], values: {} });
  }, []);

  useEffect(() => {
    getDateAndTimeLists(calFilterValues?.slotSize ?? 60, false);
  }, [openingHours, selectedDate, selectedType]);

  const onInitApiCalls = () => {
    dispatch(getClinicOpeningHours());
    dispatch(getClinicHolidays());
    dispatch(getClinicVetsUnavailability()); //to get vets leave and unavailability
    dispatch(getClinicFacilities()); // to get facilities
  };

  // useEffect(() => {
  //   if (fetchRef.current) return;
  //   if (role === "ROLE_DOCTOR") {
  //     if (!selectedClinicBranch?.branchId) return;
  //     fetchRef.current = true;
  //     dispatch(
  //       getClinicVetsWithFilter({
  //         type: ["doctor"],
  //         vetData: dataSharing?.vetData,
  //       }),
  //     );
  //     return;
  //   }
  // }, [selectedClinicBranch, role, dispatch]);

  useEffect(() => {
    const isTodayView =
      selectedType === "today" ||
      (selectedType === "date" &&
        selectedDate &&
        moment(selectedDate).isValid() &&
        moment(selectedDate).format("YYYY-MM-DD") ===
          moment().format("YYYY-MM-DD"));

    if (!isTodayView || !Array.isArray(timeList) || timeList.length === 0) {
      return;
    }

    const scrollToCurrentTime = () => {
      try {
        timeRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } catch {
        // row may be unmounted or not scrollable yet
      }
    };

    const timer = setTimeout(scrollToCurrentTime, 0);
    return () => clearTimeout(timer);
  }, [selectedType, selectedDate, timeList]);

  useEffect(() => {
    if (!expandedMoreSlot) return;
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".new-cal-more-apnts-popup") &&
        !e.target.closest(".new-cal-more-apnts-backdrop") &&
        !e.target.closest(".new-cal-more-apnts-trigger") &&
        !e.target.closest(".new-cal-menu-overlay")
      ) {
        setExpandedMoreSlot(null);
        setMorePopupAnchor(null);
        moreTriggerRef.current = null;
      }
    };
    const updatePopupPosition = () => {
      const trigger = moreTriggerRef.current;
      if (!trigger?.getBoundingClientRect) return;
      const rect = trigger.getBoundingClientRect();
      setMorePopupAnchor((prev) => (prev ? { ...prev, rect } : null));
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updatePopupPosition, true);
    window.addEventListener("resize", updatePopupPosition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePopupPosition, true);
      window.removeEventListener("resize", updatePopupPosition);
    };
  }, [expandedMoreSlot]);

  useEffect(() => {
    if (layoutMode !== LAYOUT_GRID) {
      setCalendarTableHeight(null);
      return;
    }

    const updateCalendarTableHeight = () => {
      requestAnimationFrame(() => {
        const anchorEl = calendarTableAnchorRef.current;
        if (!anchorEl) return;

        const top = anchorEl.getBoundingClientRect().top;
        const viewportHeight =
          window.visualViewport?.height ?? window.innerHeight;
        setCalendarTableHeight(Math.max(viewportHeight - top - 12, 200));
      });
    };

    updateCalendarTableHeight();

    window.addEventListener("resize", updateCalendarTableHeight);
    window.visualViewport?.addEventListener(
      "resize",
      updateCalendarTableHeight,
    );

    const observer = new ResizeObserver(updateCalendarTableHeight);
    if (calendarToolbarRef.current) {
      observer.observe(calendarToolbarRef.current);
    }
    if (calendarTableAnchorRef.current) {
      observer.observe(calendarTableAnchorRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateCalendarTableHeight);
      window.visualViewport?.removeEventListener(
        "resize",
        updateCalendarTableHeight,
      );
      observer.disconnect();
    };
  }, [
    layoutMode,
    calFilterValues?.showStats,
    selectedType,
    perspective,
    dateLabel,
  ]);

  useEffect(() => {
    if (!openMenuPortal) return;
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".new-cal-menu-overlay") &&
        !e.target.closest(".new-cal-apnt-menu-trigger")
      ) {
        handleClose(openMenuPortal.appointmentId);
      }
    };
    const updateMenuPosition = () => {
      const trigger = apntMenuTriggerRef.current;
      if (!trigger?.getBoundingClientRect) return;
      const rect = trigger.getBoundingClientRect();
      setOpenMenuPortal((prev) => (prev ? { ...prev, rect } : null));
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [openMenuPortal]);

  useEffect(() => {
    if (!openUnavailMenuPortal) return;
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".new-cal-menu-overlay") &&
        !e.target.closest(".new-cal-apnt-menu-trigger")
      ) {
        setOpenUnavailMenuPortal(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openUnavailMenuPortal]);

  const buildInternalValuesFromUnavailability = (unavailability) => {
    const date = moment(unavailability?.date).toDate();
    const startTime = moment(
      `${unavailability?.date} ${unavailability?.startTime}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
      true,
    );
    const endTime = moment(
      `${unavailability?.date} ${unavailability?.endTime}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
      true,
    );

    return {
      title: unavailability?.title ?? "",
      date,
      startTime: startTime.isValid() ? startTime.toDate() : date,
      endTime: endTime.isValid() ? endTime.toDate() : date,
      remarks: unavailability?.holliday ?? "",
      users:
        unavailability?.doctorsIds?.map((li) => {
          const vetMatch = (
            Array.isArray(clinicVetsUnavailability)
              ? clinicVetsUnavailability
              : []
          )?.find((al) => al?.doctorId === li);
          return {
            name: vetMatch?.name,
            value: li ?? vetMatch?.doctorId,
            id: li ?? vetMatch?.doctorId,
          };
        }) ?? [],
    };
  };

  const handleUnavailMenuClick = (unavailability, vet, e) => {
    e.stopPropagation();
    const rect = e.currentTarget?.getBoundingClientRect?.();
    if (!rect || !unavailability) return;
    setOpenUnavailMenuPortal({
      id: `unavail-${unavailability?.holliId}`,
      unavailability,
      vet,
      rect,
    });
  };

  const onOpenUpdateUnavailability = (unavailability, vet) => {
    setOpenUnavailMenuPortal(null);
    setUnavailEditData({
      holliId: unavailability?.holliId,
      internalValues: buildInternalValuesFromUnavailability(unavailability),
      vet: {
        doctorId: vet?.doctorId ?? vet?.value,
        name: vet?.name,
        date: moment(unavailability?.date).toDate(),
        startTime: unavailability?.startTime,
        endTime: unavailability?.endTime,
      },
    });
    setUnavailEditModVsble(true);
  };

  const onOpenDeleteUnavailability = (unavailability, vet) => {
    setOpenUnavailMenuPortal(null);
    setSelectedUnavailability({ unavailability, vet });
    setUnavailDeleteModVsble(true);
  };

  const onCloseDeleteUnavailabilityMod = () => {
    setUnavailDeleteModVsble(false);
    setSelectedUnavailability(null);
  };

  const onConfirmDeleteUnavailability = () => {
    const holliId = selectedUnavailability?.unavailability?.holliId;
    if (!holliId) return;
    dispatch(deleteVetHolidayById(holliId)).then((res) => {
      if (res?.payload) {
        dispatch(getClinicVetsUnavailability());
        onCallRightApi();
        onCloseDeleteUnavailabilityMod();
      }
    });
  };

  const onCloseUnavailEditMod = () => {
    setUnavailEditModVsble(false);
    setUnavailEditData(null);
  };

  const handleUnavailEditModClose = (e) => {
    if (e === "apiSuccess") {
      dispatch(getClinicVetsUnavailability());
      onCallRightApi();
    }
    onCloseUnavailEditMod();
  };

  const getUnavailMenuList = (unavailability, vet) => (
    <>
      <Typography
        className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
        onClick={() => onOpenUpdateUnavailability(unavailability, vet)}
      >
        Update
      </Typography>
      <Divider />
      <Typography
        className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
        onClick={() => onOpenDeleteUnavailability(unavailability, vet)}
      >
        Delete
      </Typography>
    </>
  );

  const renderUnavailMenuPortal = () => {
    if (
      !openUnavailMenuPortal?.rect ||
      !openUnavailMenuPortal?.unavailability
    ) {
      return null;
    }
    const { unavailability, vet, rect } = openUnavailMenuPortal;
    return createPortal(
      <div className="new-cal-menu-overlay" style={{ zIndex: 1399 }}>
        <div
          className="new-cal-menu-backdrop"
          onClick={() => setOpenUnavailMenuPortal(null)}
          aria-hidden="true"
        />
        <div
          className="new-cal-menu new-cal-menu-portal"
          style={getMenuPortalStyle(rect, "card")}
          onClick={(e) => e.stopPropagation()}
        >
          {getUnavailMenuList(unavailability, vet)}
        </div>
      </div>,
      document.body,
    );
  };

  useEffect(() => {
    const reqVetList = clinicVets
      ?.filter((vl) => {
        if (vl?.userType?.find((ut) => ut?.name === "ROLE_DOCTOR")) {
          return vl;
        }
      })
      ?.map((vet) => {
        return { name: vet?.name, value: vet?.doctorId, id: vet?.doctorId };
      });
    reqVetList.unshift({
      name: `${
        role === "ROLE_CLINIC" ? profile?.name : clinicDetails?.name
      } (Clinic)`,
      value: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
      id: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
    });
    reqVetList?.unshift(allObj);
    setVetDropList(reqVetList);
  }, [clinicVets]);

  useEffect(() => {
    onNewApiCall();
    // handleApiCall();
    //for storing details to call socket
    dispatch(
      storeApntmntScreenDetails({
        selectedVet,
        selectedType,
        selectedDate,
      }),
    );
  }, [
    selectedType,
    selectedDate,
    weekDate,
    calFilterValues?.showPhysical,
    calFilterValues?.showVirtual,
    calFilterValues?.showHomeVisit,
    calFilterValues?.serviceType,
    calFilterValues?.vets,
    calFilterValues?.appointmentStatus,
    calFilterValues?.facilityTypes,
  ]);

  // useEffect(() => {
  //   getReqAppointments();
  //   const reqVets = [...(appointments?.doctors ?? [])];
  //   if (
  //     role !== "ROLE_CLINIC" &&
  //     (appointments?.doctors?.length === 0 || !appointments?.doctors)
  //   ) {
  //     reqVets.push({
  //       name: profile?.name,
  //       value: profile?.id,
  //       id: profile?.id,
  //       doctorId: profile?.id,
  //     });
  //   }
  //   if (role === "ROLE_CLINIC") {
  //     reqVets.unshift({
  //       name: profile?.name,
  //       value: profile?.id,
  //       id: profile?.id,
  //       doctorId: profile?.id,
  //     });
  //   }
  //   setVetList(reqVets);
  // }, [appointmentLists, vetAppointments]);

  //  { name: "Vets", value: "Vets" },
  //                   { name: "Groomers", value: "Groomers" },
  //                   {
  //                     name: "Lab/Radiology Tech",
  //                     value: "Lab/Radiology Tech",
  //                   },

  const getVetsBySelectedRole = (forDrop = false) => {
    const reqRole = forDrop ? tempFilterValues?.roles : calFilterValues?.roles;
    const reqVetRole =
      reqRole === "Vets"
        ? "ROLE_DOCTOR"
        : reqRole === "Groomers"
          ? "ROLE_GROOMER"
          : "ROLE_RADIOLOGY";
    return (
      (clinicVets ?? [])?.filter(
        (vl) => vl?.active === "Y" && vl?.userRole === reqVetRole,
      ) ?? []
    );
  };

  const getVetListNew = (
    withFilter = false,
    forDrop = false,
    withClinic = false,
  ) => {
    const selectedVetIds = calFilterValues?.vets?.some(
      (vl) => (vl?.value ?? "")?.toString()?.toLowerCase() === "all",
    )
      ? []
      : calFilterValues?.vets?.map((vl) => vl?.doctorId);
    const profileId = profile?.id;
    const clinicEntry = withClinic
      ? {
          active: "Y",
          name: `${role === "ROLE_CLINIC" ? profile?.name : clinicDetails?.name}`,
          value: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
          id: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
          doctorId: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
          userRole: "ROLE_DOCTOR",
          actualRole: "ROLE_CLINIC",
          role: "Clinic",
        }
      : null;
    const clinicId = clinicEntry?.doctorId;

    const vetList = (getVetsBySelectedRole(forDrop) ?? [])
      ?.filter((cv) => {
        const vetIdMatched =
          withFilter && selectedVetIds?.length > 0
            ? selectedVetIds.includes(cv?.doctorId)
            : true;
        return vetIdMatched;
      })
      ?.map((vl) => ({
        name: vl?.name,
        value: vl?.doctorId,
        id: vl?.doctorId,
        doctorId: vl?.doctorId,
        userRole: vl?.userRole,
        actualRole: vl?.actualRole ?? vl?.userRole,
        role: vl?.role,
      }));

    const vetsWithoutClinicDuplicate = withClinic
      ? (vetList ?? []).filter((vl) => vl?.doctorId !== clinicId)
      : (vetList ?? []);

    const profileEntry = vetsWithoutClinicDuplicate.find(
      (vl) => vl?.doctorId === profileId,
    );
    const otherVets = vetsWithoutClinicDuplicate.filter(
      (vl) => vl?.doctorId !== profileId,
    );

    return [
      ...(clinicEntry ? [clinicEntry] : []),
      ...(profileEntry && clinicId !== profileId ? [profileEntry] : []),
      ...otherVets,
    ];
  };

  const getNewFilterObj = () => {
    const reqObj = {};
    if (selectedType === "today") {
      reqObj.startDate = moment().format("YYYY-MM-DD");
      reqObj.endDate = moment().format("YYYY-MM-DD");
    }
    if (selectedType === "date") {
      reqObj.startDate = moment(selectedDate).format("YYYY-MM-DD");
      reqObj.endDate = moment(selectedDate).format("YYYY-MM-DD");
    }
    if (selectedType === "week") {
      reqObj.startDate = getWeekRange(weekDate)?.startDate;
      reqObj.endDate = getWeekRange(weekDate)?.endDate;
    }
    if (
      calFilterValues?.showPhysical ||
      calFilterValues?.showVirtual ||
      calFilterValues?.showHomeVisit
    ) {
      reqObj.consultationType = [];
      if (calFilterValues?.showPhysical) {
        reqObj.consultationType.push("Physical");
      }
      if (calFilterValues?.showVirtual) {
        reqObj.consultationType.push("Virtual");
      }
      if (calFilterValues?.showHomeVisit) {
        reqObj.consultationType.push("Home");
      }
    }
    if (calFilterValues?.serviceType?.length > 0) {
      const isAllSelected = calFilterValues?.serviceType?.some(
        (st) => st?.value?.toLowerCase() === "all",
      );
      if (!isAllSelected) {
        reqObj.serviceType = calFilterValues?.serviceType?.map(
          (st) => st?.value,
        );
      }
    }
    if (perspective === PERSPECTIVE_FACILITY) {
      if (calFilterValues?.facilityTypes?.length > 0) {
        const isAllSelected = calFilterValues?.facilityTypes?.some(
          (st) => st?.value?.toLowerCase() === "all",
        );
        if (!isAllSelected) {
          const selectedTypes = calFilterValues?.facilityTypes
            ?.map((st) => st?.value)
            ?.filter(Boolean);
          const reqIds =
            (Array.isArray(facilities) ? facilities : [])
              ?.filter((fac) => selectedTypes?.includes(fac?.type))
              ?.map((li) => li?.facilityId) ?? [];
          reqObj.facilityId = reqIds;
        }
      }
      if (calFilterValues?.vets?.length > 0) {
        const isAllSelected = calFilterValues?.vets?.some(
          (st) => (st?.value ?? "")?.toString()?.toLowerCase() === "all",
        );
        if (!isAllSelected) {
          reqObj.vetId = calFilterValues?.vets?.map((st) => st?.value);
          const clinicVetId =
            role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id;
          if (clinicVetId) reqObj.vetId.push(clinicVetId);
        }
      }
    }
    if (calFilterValues?.appointmentStatus?.length > 0) {
      const isAllSelected = calFilterValues?.appointmentStatus?.some(
        (st) => (st?.value ?? "")?.toString()?.toLowerCase() === "all",
      );
      if (!isAllSelected) {
        reqObj.status = calFilterValues?.appointmentStatus?.map(
          (li) => statusMap[li?.value] || li?.value,
        );
      }
    }
    return reqObj;
  };

  const onNewApiCall = () => {
    dispatch(getAppointmentsWithFilter(getNewFilterObj()));
    if (selectedType === "week") {
      const reqDates = getDatesInRange(
        new Date(getWeekRange(weekDate)?.startDate),
        new Date(getWeekRange(weekDate)?.endDate),
      );
      setDateList(reqDates);
    }
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { page: currentPage, itemSize };
    if (reset) return reqObj;
    if (
      searchValues?.selectedVet?.length > 0 &&
      searchValues?.selectedVet?.some((li) => li?.value !== "all")
    ) {
      reqObj.vetId = searchValues?.selectedVet
        ?.filter((li) => li?.value !== "all")
        ?.map((li) => li?.value);
    }
    // if (searchValues?.selectedVet?.value !== "all") {
    //   reqObj.vetId = searchValues?.selectedVet?.value;
    // }
    if (searchValues?.petName?.length > 0)
      reqObj.petName = searchValues?.petName;
    // if (searchValues?.vetName?.length > 0)
    //   reqObj.vetName = searchValues?.vetName;
    if (searchValues?.consultationType?.length > 0)
      reqObj.consultationType = searchValues?.consultationType?.map(
        (li) => li?.value,
      );
    if (searchValues?.status?.length > 0)
      reqObj.status = searchValues?.status?.includes("All")
        ? ["all"]
        : searchValues?.status?.map((li) => statusMap[li?.value] || li?.value);
    if (filterValues?.selectedList?.includes("Type")) {
      reqObj.immediate = filterValues?.values?.Type === "Immediate";
    }
    if (filterValues?.values?.["Customer Name"]?.length > 0) {
      reqObj.username = filterValues?.values?.["Customer Name"];
    }
    if (filterValues?.selectedList?.includes("Service Type")) {
      reqObj.serviceType =
        filterValues?.values?.["Service Type"]?.map((li) => li?.value) ?? [];
    }
    if (filterValues?.selectedList?.includes("Appt Date")) {
      reqObj.startDate = moment(
        new Date(filterValues?.values?.["Start Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endDate = moment(
        new Date(filterValues?.values?.["End Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Appt Time")) {
      reqObj.startTime = moment(
        new Date(filterValues?.values?.["Start Time"] ?? new Date()),
      ).format("HH:mm");
      reqObj.endTime = moment(
        new Date(filterValues?.values?.["End Time"] ?? new Date()),
      ).format("HH:mm");
    }
    return reqObj;
  };

  useEffect(() => {
    if (layoutMode === LAYOUT_LIST) {
      onCalendarApiCall(1);
    }
  }, [
    searchValues?.petName,
    searchValues?.consultationType,
    searchValues?.status,
    searchValues?.selectedVet,
    layoutMode,
  ]);

  const onCalendarApiCall = (currentPage = 1, reset = false) => {
    dispatch(
      getAppointmentsByClinicAndFilter(getFilterObj(currentPage, reset)),
    );
  };

  // const handleApiCall = () => {
  //   if (selectedVet?.value === "all") {
  //     let url = `?type=${selectedType}`;
  //     if (selectedType === "date") {
  //       url = url + `&date=${moment(selectedDate).format("YYYY-MM-DD")}`;
  //     }
  //     if (selectedType === "week") {
  //       url = `${url}&currentDate=${getWeekRange(weekDate)?.startDate}`;
  //       const reqDates = getDatesInRange(
  //         new Date(getWeekRange(weekDate)?.startDate),
  //         new Date(getWeekRange(weekDate)?.endDate),
  //       );
  //       setDateList(reqDates);
  //     }
  //     dispatch(getAppointsmentsByClinic(url));
  //   } else {
  //     let url = `${selectedVet?.value}?type=${selectedType}`;
  //     if (selectedType === "date") {
  //       url = url + `&date=${moment(selectedDate).format("YYYY-MM-DD")}`;
  //     }
  //     if (selectedType === "week") {
  //       url = `${url}&currentDate=${getWeekRange(weekDate)?.startDate}`;
  //       const reqDates = getDatesInRange(
  //         new Date(getWeekRange(weekDate)?.startDate),
  //         new Date(getWeekRange(weekDate)?.endDate),
  //       );
  //       setDateList(reqDates);
  //     }
  //     dispatch(getVetAppointmentsByIdAndFilter(url));
  //   }
  // };

  const getVetList = () => {
    return clinicVets
      ?.filter((vl) => {
        if (vl?.userType?.find((ut) => ut?.name === "ROLE_DOCTOR")) {
          return vl;
        }
      })
      ?.map((vet) => {
        return { name: vet?.name, value: vet?.doctorId, id: vet?.doctorId };
      });
  };

  const checkIsVet = (apntVetId = null) => {
    if (!apntVetId) return false;
    return clinicVets?.some((li) =>
      li?.doctorId === apntVetId && li?.userType?.length > 0
        ? li?.userType?.some((ut) => ut?.name === "ROLE_DOCTOR")
        : false,
    );
  };

  const canBookAppointmentForVet = (vet) => {
    if (role === "ROLE_CLINIC") return true;
    if (role === "ROLE_DOCTOR") {
      const vetId = vet?.doctorId ?? vet?.value ?? vet?.id;
      return vetId === profile?.id;
    }
    return true;
  };

  const isApntCancelled = (apnt) => {
    if (!apnt?.appointmentStatus) return false;
    const status = String(apnt.appointmentStatus).toLowerCase();
    return status === "canceled" || status === "cancelled";
  };

  const getAppointmentsByVet = (list = null, vetId = null) => {
    const sourceList = list ?? appointmentsWithFilter?.data ?? [];
    if (!Array.isArray(sourceList)) return vetId ? [] : {};
    const reqObj = groupBy(sourceList, "doctorId");
    if (vetId != null && vetId !== "") {
      return reqObj?.[vetId] ?? reqObj?.[String(vetId)] ?? [];
    }
    return reqObj ?? {};
  };

  const formatFacilityTypeLabel = (type) => {
    if (!type) return "Facility";
    return String(type)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ");
  };

  const getFacilityList = () => {
    const selectedTypes = calFilterValues?.facilityTypes ?? [];
    const isAllSelected =
      !selectedTypes?.length ||
      selectedTypes?.some(
        (st) => (st?.value ?? "")?.toString()?.toLowerCase() === "all",
      );
    const typeValues = isAllSelected
      ? []
      : selectedTypes?.map((st) => st?.value)?.filter(Boolean);

    return (
      (Array.isArray(facilities) ? facilities : [])
        ?.filter((fl) => (fl?.type ?? "")?.toLowerCase() !== "devices")
        ?.filter((fl) =>
          typeValues?.length > 0 ? typeValues.includes(fl?.type) : true,
        )
        ?.map((fl) => ({
          name: fl?.name,
          value: fl?.facilityId,
          id: fl?.facilityId,
          facilityId: fl?.facilityId,
          type: fl?.type,
          role: formatFacilityTypeLabel(fl?.type),
        })) ?? []
    );
  };

  const getAppointmentsByFacility = (list = null, facilityId = null) => {
    const sourceList = list ?? appointmentsWithFilter?.data ?? [];
    if (!Array.isArray(sourceList)) return facilityId ? [] : {};
    const reqObj = groupBy(sourceList, "facilityId");
    if (facilityId != null && facilityId !== "") {
      return reqObj?.[facilityId] ?? reqObj?.[String(facilityId)] ?? [];
    }
    return reqObj ?? {};
  };

  const getApntDateStr = (apnt) => {
    if (!apnt) return null;
    if (apnt?.appointmentDate) return apnt?.appointmentDate;
    if (!apnt?.appointmentdateTime) return null;
    const parsed = moment(apnt?.appointmentdateTime);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
  };

  const getApntStartTime = (apnt) => {
    if (!apnt) return null;
    const raw =
      apnt?.appointmentStartTime ??
      apnt?.startTime ??
      (apnt?.appointmentdateTime
        ? moment(apnt.appointmentdateTime).format("HH:mm")
        : null);
    if (!raw || raw === "Invalid date") return null;
    const parsed = moment(raw, ["HH:mm", "H:mm"], true);
    return parsed.isValid() ? parsed.format("HH:mm") : null;
  };

  const getNextAppointmentTimeFromList = (apntList) => {
    try {
      if (!Array.isArray(apntList) || apntList.length === 0) return null;

      const todayStr = moment().format("YYYY-MM-DD");
      const selectedMoment =
        selectedType === "today" ? moment() : moment(selectedDate);
      if (!selectedMoment.isValid()) return null;

      const selectedDateStr = selectedMoment.format("YYYY-MM-DD");
      if (selectedDateStr < todayStr) return null;

      const currTime = moment().format("HH:mm");
      const isToday = selectedDateStr === todayStr;

      const sortedApnts = (apntList ?? [])
        ?.filter((apnt) => {
          if (!apnt || isApntCancelled(apnt)) return false;
          if (getApntDateStr(apnt) !== selectedDateStr) return false;
          return Boolean(getApntStartTime(apnt));
        })
        ?.sort((a, b) => {
          const timeA = getApntStartTime(a) || "";
          const timeB = getApntStartTime(b) || "";
          return timeA.localeCompare(timeB);
        });

      const nextApnt = sortedApnts.find((apnt) => {
        const startTime = getApntStartTime(apnt);
        if (!startTime) return false;
        return isToday ? startTime > currTime : true;
      });

      if (!nextApnt) return null;

      const formatted = moment(getApntStartTime(nextApnt), "HH:mm", true);
      return formatted.isValid() ? formatted.format("hh:mm A") : null;
    } catch {
      return null;
    }
  };

  const getNextAppointmentTime = (vetId) => {
    if (!vetId) return null;
    return getNextAppointmentTimeFromList(getAppointmentsByVet(null, vetId));
  };

  const getNextFacilityAppointmentTime = (facilityId) => {
    if (!facilityId) return null;
    return getNextAppointmentTimeFromList(
      getAppointmentsByFacility(null, facilityId),
    );
  };

  const getAppointmentsByDate = () => {
    return groupBy(appointmentsWithFilter?.data ?? [], "appointmentDate");
  };

  const getAppointmentsByTime = (list = []) => {
    const reqList = [];
    const slotSize = calFilterValues?.slotSize ?? 60;
    timeList?.forEach((tm) => {
      list?.forEach((sl) => {
        const apntTime = getApntStartTime(sl);
        const timeBet = apntTime && isTimeInSlot(apntTime, tm, slotSize);
        if (timeBet) {
          reqList.push({ ...sl, time: getSlotTimeRangeKey(tm, slotSize) });
        }
      });
    });
    return groupBy(reqList, "time");
  };

  const getReqAppointments = (list = null, storeState = true) => {
    const listToFilter = list
      ? list
      : selectedVet?.value === "all"
        ? appointmentLists
        : vetAppointments;
    const reqList = [];
    const slotSize = calFilterValues?.slotSize ?? 60;
    timeList?.forEach((tm) => {
      listToFilter?.forEach((sl) => {
        const apntTime = getApntStartTime(sl);
        const timeBet = apntTime && isTimeInSlot(apntTime, tm, slotSize);
        if (timeBet) {
          reqList.push({ ...sl, time: getSlotTimeRangeKey(tm, slotSize) });
        }
      });
    });
    const reqData = groupBy(reqList, "time");
    if (storeState) {
      setReqApnts(reqData);
    } else {
      return reqData;
    }
  };

  const getActiveCalendarDate = () =>
    selectedType === "today" ? new Date() : selectedDate;

  const getOpeningSlotRange = (openings, date) => {
    if (!Array.isArray(openings) || !date || !moment(date).isValid()) {
      return null;
    }

    const dayName = moment(date).format("dddd").toLowerCase();
    const dayOpening = openings.find(
      (hr) =>
        (hr?.day ?? "").toLowerCase() === dayName &&
        hr?.status &&
        Array.isArray(hr?.times) &&
        hr.times.length > 0,
    );

    if (!dayOpening) return null;

    const startTime = dayOpening.times.reduce((min, slot) => {
      const slotStart = slot?.startTime;
      if (!slotStart) return min;
      return !min || slotStart < min ? slotStart : min;
    }, null);

    const endTime = dayOpening.times.reduce((max, slot) => {
      const slotEnd = slot?.endTime;
      if (!slotEnd) return max;
      return !max || slotEnd > max ? slotEnd : max;
    }, null);

    if (!startTime || !endTime) return null;

    return { startTime, endTime };
  };

  const getDateAndTimeLists = (tme = 60, isFirstTime = true) => {
    const openingRange = getOpeningSlotRange(
      openingHours,
      getActiveCalendarDate(),
    );
    const reqTimes = createTimeSlots(
      openingRange?.startTime ?? "00:00",
      openingRange?.endTime ?? "23:59",
      tme,
    );
    setTimeList(reqTimes);
    if (isFirstTime) {
      const dates = getDateList();
      setDateList(dates);
    }
  };

  const closeAllMenus = () => {
    setAnchorEls({});
    setOpenMenuPortal(null);
    setOpenUnavailMenuPortal(null);
    apntMenuTriggerRef.current = null;
  };

  const handleClick = (id, e, apnt, variant = "card") => {
    const trigger = e?.currentTarget ?? null;
    const rect = trigger?.getBoundingClientRect?.();
    apntMenuTriggerRef.current = trigger;
    setAnchorEls({ [id]: true });
    if (rect && apnt) {
      setOpenMenuPortal({ appointmentId: id, apnt, rect, variant });
    }
  };

  const handleClose = (id) => {
    setAnchorEls((prev) => ({ ...prev, [id]: false }));
    setOpenMenuPortal((prev) => {
      if (!id || prev?.appointmentId === id) {
        apntMenuTriggerRef.current = null;
        return null;
      }
      return prev;
    });
  };

  const handleSelectWeek = () => {
    setWeekAnchor(getWeekRange(new Date())?.startDate);
    setWeekDate(getWeekRange(new Date())?.startDate);
    setSelectedType("week");
    closeAllMenus();
  };

  const navDetailsPage = (det) => {
    // if (det?.appointmentId) {
    //   dispatch(getPetsCompliantSummary(det?.appointmentId));
    // } else if (det?.petId) {
    //   dispatch(getPetDetailsById(det?.petId));
    // }
    navigate(`/clinic-pet-details`, {
      state: { appointment: { appoinment: det } },
    });
  };

  const getFilteredList = (list) => {
    const reqList =
      list?.filter((li) => {
        const conTypMatch = conTypes?.includes(li?.appointmentType);
        const serMatch = sleServiceTypes?.includes("All")
          ? true
          : sleServiceTypes?.includes(li?.serviceType);
        return conTypMatch && serMatch;
      }) ?? [];
    return reqList ?? [];
  };

  const handleSelectDay = () => {
    setSelectedType("date");
    closeAllMenus();
  };

  const onCloseCancelMod = () => {
    setSlctedApnt(null);
    setCanModVsble(false);
    closeAllMenus();
    setCancelReason("");
  };

  const onCloseNoShowMod = () => {
    setNoShowModVsble(false);
    setSlctedApnt(null);
    closeAllMenus();
  };

  const conCancel = () => {
    dispatch(
      updateCancel({
        appId: slctedApnt?.appointmentId,
        reason: cancelReason,
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseCancelMod();
      }
    });
  };

  const onConfirmNoShow = () => {
    if (!slctedApnt?.appointmentId) return;
    dispatch(
      updateAppointmentStatusById({
        appointmentId: slctedApnt?.appointmentId,
        status: "noshow",
        successMsg: "No show added successfully!",
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseNoShowMod();
        onCallRightApi();
        // handleApiCall();
        closeAllMenus();
      }
    });
  };

  const handleCancel = (apnt) => {
    setSlctedApnt(apnt);
    setCanModVsble(true);
  };

  const handleNoshow = (apnt) => {
    setSlctedApnt(apnt);
    setNoShowModVsble(true);
  };

  const handleReschdule = (apnt) => {
    const dateStr = getApntDateStr(apnt);
    setModValues({
      ...modValues,
      date: dateStr ? new Date(dateStr) : new Date(),
      appointmentOption: "Later",
      vet: vetDropList?.find((vl) => vl?.value === apnt?.doctorId),
    });
    setSlctedApnt({ ...apnt, reAssign: false });
    setReSchReAssModVsble(true);
  };

  const handleReassign = (apnt) => {
    const dateStr = getApntDateStr(apnt);
    setModValues({
      ...modValues,
      date: dateStr ? new Date(dateStr) : new Date(),
      appointmentOption: "Later",
      vet: vetDropList?.find((vl) => vl?.value === apnt?.doctorId),
    });
    setSlctedApnt({ ...apnt, reAssign: true });
    setReSchReAssModVsble(true);
  };

  const addApntToInTransit = (
    apnt,
    vetId = null,
    facilityId = null,
    facilityName = null,
  ) => {
    dispatch(
      updateAppointmentStatusById({
        appointmentId: apnt?.appointmentId,
        status: "intransit",
        successMsg: "Patient has been notified!",
        url: `&facilityId=${facilityId}&facilityName=${facilityName}&doctorId=${vetId}`,
      }),
    ).then((res) => {
      if (res?.payload) {
        // const data = { facilityId, facilityName, doctorId: vetId };
        // dispatch(
        //   clinicReassignAppointment({
        //     appointmentId: apnt?.appointmentId,
        //     data,
        //   })
        // ).then((reRes) => {
        //   if (reRes?.payload) {
        //     onCloseAssignVetMod();
        //     handleApiCall();
        //     closeAllMenus();
        //     return;
        //   } else {
        //     onCloseAssignVetMod();
        //   }
        // });
        onCloseAssignVetMod();
        onCallRightApi();
        // handleApiCall();
        closeAllMenus();
      }
    });
  };

  const onCheckInTransit = (apnt) => {
    if (apnt?.appointmentType === "Home") {
      if (
        (clinicDetails?.id === apnt?.doctorId && !checkIsVet(profile?.id)) ||
        !apnt?.facilityId
      ) {
        const reqVet =
          clinicDetails?.id === apnt?.doctorId && !checkIsVet(profile?.id)
            ? null
            : clinicVets?.find(
                (vl) =>
                  vl?.doctorId ===
                  (checkIsVet(apnt?.doctorId)
                    ? apnt?.doctorId
                    : checkIsVet(profile?.id)
                      ? profile?.id
                      : null),
              );
        setAssignVetValues({
          vet:
            clinicDetails?.id === apnt?.doctorId && !checkIsVet(profile?.id)
              ? null
              : {
                  name: reqVet?.name ?? apnt?.doctorName,
                  value: reqVet?.doctorId ?? apnt?.doctorId,
                  id: reqVet?.doctorId ?? apnt?.doctorId,
                },
          facility: apnt?.facilityId
            ? {
                name: apnt?.facility ?? "",
                value: apnt?.facilityId,
                id: apnt?.facilityId,
              }
            : null,
        });
        setSlctedApnt(apnt);
        setAssignVetModVisible(true);
        return;
      } else {
        addApntToInTransit(
          apnt,
          checkIsVet(profile?.id) ? profile?.id : apnt?.doctorId,
          apnt?.facilityId,
          apnt?.facility,
        );
      }
    } else {
      const data = {
        appointmentId: apnt?.appointmentId,
        doctorId: apnt?.doctorId,
      };
      dispatch(checkedInApi(data)).then((res) => {
        if (res?.payload) {
          onCallRightApi();
          // handleApiCall();
          closeAllMenus();
        }
      });
    }
  };

  const onCloseAdmitMod = (type = "close") => {
    if (type !== "close") {
      onCallRightApi();
      // handleApiCall();
    }
    setAdmitModVsble(false);
    setSlctedApnt(null);
    closeAllMenus();
  };

  const handleAdmit = (apnt) => {
    setSlctedApnt(apnt);
    setAdmitModVsble(true);
  };

  const onCloseReSchReAss = () => {
    setReSchReAssModVsble(false);
    setSlctedApnt(null);
    setModErrs({});
    closeAllMenus();
  };

  const onFinalize = (apnt) => {
    dispatch(updateFinalize(apnt?.appointmentId)).then((res) => {
      if (res?.payload) {
        onCallRightApi();
        // handleApiCall();
        closeAllMenus();
      }
    });
  };

  const onCallRightApi = () => {
    if (layoutMode === LAYOUT_GRID) {
      onNewApiCall();
    } else {
      onCalendarApiCall(page);
    }
  };

  const addApntToConsultation = (
    apnt,
    vetId = null,
    facilityId = null,
    facilityName = null,
  ) => {
    const data = {
      appointmentId: apnt?.appointmentId,
      doctorId: vetId ?? apnt?.doctorId,
      facilityId,
      facilityName,
    };
    dispatch(checkedCallInApi(data)).then((res) => {
      if (res?.payload) {
        // const data = { facilityId, facilityName, doctorId: vetId };
        // dispatch(
        //   clinicReassignAppointment({
        //     appointmentId: apnt?.appointmentId,
        //     data,
        //   }),
        // ).then((reRes) => {
        //   if (reRes?.payload) {
        //     onCloseAssignVetMod();
        //     handleApiCall();
        //     closeAllMenus();
        //     return;
        //   } else {
        //     onCloseAssignVetMod();
        //   }
        // });
        onCloseAssignVetMod();
        onCallRightApi();
        // handleApiCall();
        closeAllMenus();
      }
    });
  };

  const handleConsult = (apnt) => {
    if (apnt?.appointmentType === "Home") {
      dispatch(
        updateAppointmentStatusById({
          appointmentId: apnt?.appointmentId,
          status: "consultation",
          successMsg: "Status updated successfully!",
        }),
      ).then((res) => {
        if (res?.payload) {
          onCallRightApi();
          // handleApiCall();
          closeAllMenus();
        }
      });
    } else {
      if (
        (clinicDetails?.id === apnt?.doctorId && !checkIsVet(profile?.id)) ||
        !apnt?.facilityId
      ) {
        const reqVet =
          clinicDetails?.id === apnt?.doctorId && !checkIsVet(profile?.id)
            ? null
            : clinicVets?.find(
                (vl) =>
                  vl?.doctorId ===
                  (checkIsVet(apnt?.doctorId)
                    ? apnt?.doctorId
                    : checkIsVet(profile?.id)
                      ? profile?.id
                      : null),
              );
        setAssignVetValues({
          vet:
            clinicDetails?.id === apnt?.doctorId && !checkIsVet(profile?.id)
              ? null
              : {
                  name: reqVet?.name ?? apnt?.doctorName,
                  value: reqVet?.doctorId ?? apnt?.doctorId,
                  id: reqVet?.doctorId ?? apnt?.doctorId,
                },
          facility: apnt?.facilityId
            ? {
                name: apnt?.facility ?? "",
                value: apnt?.facilityId,
                id: apnt?.facilityId,
              }
            : null,
        });
        setSlctedApnt(apnt);
        setAssignVetModVisible(true);
        return;
      } else {
        addApntToConsultation(
          apnt,
          checkIsVet(profile?.id) ? profile?.id : apnt?.doctorId,
          apnt?.facilityId,
          apnt?.facility,
        );
      }
    }
  };

  const onCloseFlowMod = () => {
    setFlowModVsble(false);
    setSlctedApnt(null);
    setModValues({});
    setModErrs({});
    closeAllMenus();
  };

  const onCloseBookFlowMod = () => {
    setFlowModVsble(false);
    setReSchReAssModVsble(false);
    setSlctedApnt(null);
    setModValues({});
    setModErrs({});
    closeAllMenus();
  };

  const onFollowUp = (apnt) => {
    setModValues({
      ...modValues,
      bookingType: "Phone",
      consultationMode: "Physical",
      serviceType: null,
      reason: "",
      vet: null,
      date: new Date(),
    });
    setSlctedApnt(apnt);
    setFlowModVsble(true);
  };

  const handleCheckout = (apnt) => {
    dispatch(updateCompleted(apnt?.appointmentId)).then((res) => {
      if (res?.payload) {
        if (
          profile?.createdBy === superTailsClinicId ||
          profile?.id === superTailsClinicId
        ) {
          dispatch(
            getMedicalHistoryByPetId({
              petId: apnt?.petId,
              // url: `?type=all&page=1&itemSize=5`,
            }),
          ).then((res2) => {
            if (res2?.payload?.data) {
              setSelectedHistory(res2?.payload?.data?.[0]);
              setPetData({
                petName: apnt?.petName,
                breed: apnt?.breed,
                gender: apnt?.gender,
                dob: apnt?.dob,
                userName: apnt?.userName,
                petData: apnt?.mobile,
              });
            }
          });
        }
        onCallRightApi();
        // handleApiCall();
        closeAllMenus();
      }
    });
  };

  const onOpenBookApnt = (vet, dat = null, time, facility = null) => {
    if (vet && !canBookAppointmentForVet(vet)) return;
    setModValues({
      ...modValues,
      bookingType: "Phone",
      consultationMode: "Physical",
      vet: vet
        ? {
            name: vet?.name,
            value: vet?.doctorId ?? vet?.value,
            doctorId: vet?.doctorId ?? vet?.value,
          }
        : null,
      facility: facility
        ? {
            name: facility?.name,
            value: facility?.facilityId ?? facility?.value,
            id: facility?.facilityId ?? facility?.value,
          }
        : null,
      pet: null,
      date: dat ?? new Date(),
      startTime: time ?? new Date(),
    });
    setApntBookModVsble(true);
  };

  const onOpenBookApntForFacility = (facility, dat = null, time) => {
    onOpenBookApnt(null, dat, time, facility);
  };

  const onCloseBookMod = () => {
    setApntBookModVsble(false);
    setModValues({});
    setModErrs({});
  };

  const handleBookModClose = (e) => {
    if (e === "apiSuccess") onCallRightApi();
  };

  const handleUpdateWeight = (apnt) => {
    setModValues(apnt);
    setUpdateWeightVsble(true);
  };

  const normalizeMenuApnt = (apnt) => {
    if (!apnt) return apnt;
    const merged = { ...(apnt?.appoinment ?? {}), ...apnt };
    const dateStr = getApntDateStr(merged);
    return {
      ...merged,
      appointmentDate: merged?.appointmentDate ?? dateStr,
    };
  };

  const runMenuAction = (action) => (apnt, e) => {
    e?.stopPropagation?.();
    const normalizedApnt = normalizeMenuApnt(apnt);
    const fromMorePopup = openMenuPortal?.variant === "popup";
    closeAllMenus();
    if (fromMorePopup) {
      closeMoreSlotPopup();
    }
    setTimeout(() => action(normalizedApnt), 0);
  };

  const menuAction = (action, apnt) => (e) => runMenuAction(action)(apnt, e);

  const getMenuList = (apnt) => {
    const status = apnt?.appointmentStatus?.toLowerCase();
    const apntType = apnt?.appointmentType;
    switch (status) {
      case "scheduled": {
        return (
          <>
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(onCheckInTransit, apnt)}
            >
              {apntType === "Home" ? "In Transit" : "Check-in"}
            </Typography>
            {apntType === "Home" ? (
              <>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleUpdateWeight, apnt)}
                >
                  Update Weight
                </Typography>
              </>
            ) : null}
            <Divider />
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleCancel, apnt)}
            >
              Cancel
            </Typography>
            <Divider />
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleReschdule, apnt)}
            >
              Reschedule/Reassign
            </Typography>
            {apntType === "Physical" && (
              <>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleAdmit, apnt)}
                >
                  Admit
                </Typography>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleNoshow, apnt)}
                >
                  No Show
                </Typography>
              </>
            )}
          </>
        );
      }
      case "inprogress": {
        return (
          <>
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleReschdule, apnt)}
            >
              Reschedule/Reassign
            </Typography>
            <Divider />
            {/* <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={() => handleReassign(apnt)}
            >
              Reassign
            </Typography>
            <Divider /> */}
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleConsult, apnt)}
            >
              {apntType === "Physical" ? "Send to Consult" : "In Consult"}
            </Typography>
            {apntType === "Physical" && (
              <>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleUpdateWeight, apnt)}
                >
                  Update Weight
                </Typography>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleCancel, apnt)}
                >
                  Cancel
                </Typography>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleAdmit, apnt)}
                >
                  Admit
                </Typography>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleNoshow, apnt)}
                >
                  No Show
                </Typography>
              </>
            )}
          </>
        );
      }
      case "intransit": {
        return (
          <>
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleReschdule, apnt)}
            >
              Reschedule/Reassign
            </Typography>
            <Divider />
            {/* <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={() => handleReassign(apnt)}
            >
              Reassign
            </Typography>
            <Divider /> */}
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleConsult, apnt)}
            >
              {apntType === "Physical" ? "Send to Consult" : "In Consult"}
            </Typography>
            {apntType === "Physical" && (
              <>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleCancel, apnt)}
                >
                  Cancel
                </Typography>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleAdmit, apnt)}
                >
                  Admit
                </Typography>
              </>
            )}
          </>
        );
      }
      case "consultation": {
        return (
          <>
            {/* <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={() => onFinalize(apnt)}
            >
              Finalize
            </Typography> */}
            {/* <Divider /> */}
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(onFollowUp, apnt)}
            >
              Follow-up
            </Typography>
            {apntType === "Physical" && (
              <>
                <Divider />
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleAdmit, apnt)}
                >
                  Admit
                </Typography>
              </>
            )}
            <Divider />
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleReassign, apnt)}
            >
              Reassign
            </Typography>
          </>
        );
      }
      case "finalize": {
        return (
          <>
            {
              // apnt?.checkOut === "Y"

              (
                (clinicDetails?.billingMode ?? "")?.toLowerCase() === "separate"
                  ? apnt?.pharmacyCheckout !== "Y" ||
                    apnt?.petShopCheckout !== "Y" ||
                    apnt?.checkOut !== "Y"
                  : apnt?.checkOut !== "Y"
              ) ? (
                <Typography
                  className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
                  onClick={menuAction(handleCheckout, apnt)}
                >
                  Checkout
                </Typography>
              ) : null
            }
            <Divider />
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(onFollowUp, apnt)}
            >
              Follow-up
            </Typography>
            <Divider />
            <Typography
              className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
              onClick={menuAction(handleReassign, apnt)}
            >
              Reassign
            </Typography>
          </>
        );
      }
      case "completed": {
        return (
          <Typography
            className="font-medium fs14 ph10 pv5 cursor txt-ali-left"
            onClick={menuAction(onFollowUp, apnt)}
          >
            Follow-up
          </Typography>
        );
      }
    }
  };

  const handleUploadComplete = async (url, apntId) => {
    await api({ contentType: true, auth: true })
      .get(`/appointment/${apntId}`)
      .then(async (getRes) => {
        if (getRes?.status === 200 || getRes?.status === 201) {
          if (getRes?.data?.data) {
            const reqObj = {
              appointment: {
                ...getRes?.data?.data,
                // caseSummary: url
              },
            };
            try {
              // await superTailsApi.post("/appointment-completed", reqObj);
              setSelectedHistory(null);
              setPetData(null);
            } catch (supErr) {
              console.log("SuperTail Api Error", supErr);
            }
          }
        }
      });
  };

  const onCloseAssignVetMod = () => {
    setAssignVetModVisible(false);
    setAssignVetValues({});
    setSlctedApnt(null);
  };

  const onAssignVetChange = (name, value, errName) => {
    setAssignVetValues({
      ...assignVetvalues,
      [name]: value,
      [errName]: value?.value ? false : true,
    });
  };

  const getFacilitiesByType = (type) => {
    const reqType =
      slctedApnt?.appointmentType === "Home"
        ? "HomeKit"
        : type === "Surgery"
          ? "OperationTheatre"
          : type === "Consultation" ||
              type === "Lab Tests" ||
              type === "Radiology Tests" ||
              type === "Others"
            ? "ConsultationRoom"
            : "BathBays";
    const reqVetList =
      facilities
        ?.filter((fl) => fl?.type?.toLowerCase() !== "devices")
        // ?.filter((fl) => fl?.type === reqType)
        ?.map((fl) => {
          return { name: fl?.name, value: fl?.facilityId, id: fl?.facilityId };
        }) ?? [];
    return reqVetList;
  };

  const onAssignVet = () => {
    if (!assignVetvalues?.vet?.value || !assignVetvalues?.facility?.value) {
      return setAssignVetValues({
        ...assignVetvalues,
        error: !assignVetvalues?.vet?.value,
        facilityErr: !assignVetvalues?.facility?.value,
      });
    }
    if (slctedApnt?.appointmentType === "Physical") {
      addApntToConsultation(
        slctedApnt,
        assignVetvalues?.vet?.value,
        assignVetvalues?.facility?.value,
        assignVetvalues?.facility?.name,
      );
    } else {
      addApntToInTransit(
        slctedApnt,
        assignVetvalues?.vet?.value,
        assignVetvalues?.facility?.value,
        assignVetvalues?.facility?.name,
      );
    }
  };

  const handleSelectToday = () => {
    const today = new Date();
    setSelectedType("today");
    setSelectedDate(today);
    setWeekAnchor(today);
  };

  const onChangeSelectedDate = (e) => {
    const { value } = e?.target ?? {};
    if (!value) return;
    const nextDate =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (!moment(nextDate).isValid()) return;
    setSelectedDate(nextDate);
    setSelectedType("date");
  };

  const completionPercent = Math.round(
    (SUMMARY_STATS.todayCompleted / SUMMARY_STATS.todayTotal) * 100,
  );

  const handlePrev = () => {
    if (selectedType === "week") {
      setWeekDate(new Date(getWeekRange(weekAnchor, -1).startDate));
      setWeekAnchor(new Date(getWeekRange(weekAnchor, -1).startDate));
      return;
    }
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev);
  };

  const handleNext = () => {
    if (selectedType === "week") {
      setWeekDate(new Date(getWeekRange(weekAnchor, 1).startDate));
      setWeekAnchor(new Date(getWeekRange(weekAnchor, 1).startDate));
      return;
    }
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next);
  };

  const onNavVetPage = (vt) => {
    if (vt?.actualRole === "ROLE_CLINIC") return;
    navigate("/vet-calendar", {
      state: { vet: { doctorId: vt?.doctorId } },
    });
  };

  const sortAppointmentsByOldest = (appointments) =>
    [...(appointments ?? [])].sort((a, b) => {
      const dateA = getApntDateStr(a) || "";
      const dateB = getApntDateStr(b) || "";
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = getApntStartTime(a) || "";
      const timeB = getApntStartTime(b) || "";
      return timeA.localeCompare(timeB);
    });

  const getApntCellDisplay = (appointments, vetCount) => {
    const sorted = sortAppointmentsByOldest(appointments);
    const total = sorted.length;
    if (total === 0) return { visible: [], hiddenCount: 0 };

    if (vetCount > CAL_MULTI_CARD_MAX_VETS) {
      if (total <= 1) return { visible: sorted, hiddenCount: 0 };
      return { visible: sorted.slice(0, 1), hiddenCount: total - 1 };
    }

    const visibleCount = getMaxVisibleApntCards(total, vetCount);

    if (total <= visibleCount) {
      return { visible: sorted, hiddenCount: 0 };
    }

    return {
      visible: sorted.slice(0, visibleCount),
      hiddenCount: total - visibleCount,
    };
  };

  const toggleMoreSlot = (slotKey, e, appointments) => {
    e?.stopPropagation?.();
    if (expandedMoreSlot === slotKey) {
      setExpandedMoreSlot(null);
      setMorePopupAnchor(null);
      moreTriggerRef.current = null;
      return;
    }
    const trigger = e?.currentTarget ?? null;
    const rect = trigger?.getBoundingClientRect?.();
    moreTriggerRef.current = trigger;
    setExpandedMoreSlot(slotKey);
    setMorePopupAnchor(rect ? { appointments, rect } : null);
  };

  const closeMoreSlotPopup = () => {
    setExpandedMoreSlot(null);
    setMorePopupAnchor(null);
    moreTriggerRef.current = null;
  };

  const closeMoreSlot = (e) => {
    e?.stopPropagation?.();
    if (openMenuPortal?.appointmentId) {
      handleClose(openMenuPortal.appointmentId);
    }
    closeMoreSlotPopup();
  };

  const getMorePopupLayout = (rect) => {
    const VIEWPORT_PADDING = 12;
    const GAP = 6;
    const HEADER_HEIGHT = 44;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const popupWidth = Math.min(340, viewportWidth - VIEWPORT_PADDING * 2);

    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
    const openAbove = spaceAbove >= spaceBelow;

    const availableHeight = (openAbove ? spaceAbove : spaceBelow) - GAP;
    const maxPopupHeight = Math.max(
      180,
      Math.min(availableHeight, viewportHeight - VIEWPORT_PADDING * 2),
    );
    const listMaxHeight = Math.max(120, maxPopupHeight - HEADER_HEIGHT);

    let top;
    if (openAbove) {
      top = Math.max(VIEWPORT_PADDING, rect.top - GAP - maxPopupHeight);
    } else {
      top = Math.min(
        rect.bottom + GAP,
        viewportHeight - VIEWPORT_PADDING - maxPopupHeight,
      );
    }

    let left = rect.right - popupWidth;
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, viewportWidth - VIEWPORT_PADDING - popupWidth),
    );

    return {
      popupStyle: {
        position: "fixed",
        top,
        left,
        width: popupWidth,
        maxHeight: maxPopupHeight,
      },
      listStyle: {
        maxHeight: listMaxHeight,
      },
    };
  };

  const renderMoreApntsPopup = () => {
    if (!expandedMoreSlot || !morePopupAnchor) return null;
    const { appointments, rect } = morePopupAnchor;
    const sortedAppointments = sortAppointmentsByOldest(appointments);
    const { popupStyle, listStyle } = getMorePopupLayout(rect);
    return createPortal(
      <div className="new-cal-more-apnts-overlay">
        <div
          className="new-cal-more-apnts-backdrop"
          onClick={closeMoreSlot}
          aria-hidden="true"
        />
        <div
          className="new-cal-more-apnts-popup"
          style={popupStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="new-cal-more-apnts-header flex-row-between-align-center">
            <Typography className="fs12 font-bold black">
              {appointments?.length} appointments
            </Typography>
            <CloseIcon
              className="cursor"
              sx={{ fontSize: 18, color: "#666" }}
              onClick={closeMoreSlot}
            />
          </div>
          <div className="new-cal-more-apnts-list" style={listStyle}>
            {sortedAppointments.map((timMa) =>
              renderCalendarApntCard(
                timMa,
                sortedAppointments.length,
                "w100Per",
                true,
              ),
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const getMenuPortalStyle = (rect, variant) => {
    const menuZIndex = variant === "popup" ? 1501 : 1400;
    const base = {
      position: "fixed",
      zIndex: menuZIndex,
      right: window.innerWidth - rect.right,
    };
    if (variant === "card") {
      return {
        ...base,
        top: rect.top - 4,
        transform: "translateY(-100%)",
      };
    }
    return {
      ...base,
      top: rect.bottom + 4,
    };
  };

  const renderApntMenuPortal = () => {
    if (!openMenuPortal?.rect || !openMenuPortal?.apnt) return null;
    const { apnt, rect, variant, appointmentId } = openMenuPortal;
    const overlayZIndex = variant === "popup" ? 1500 : 1399;
    return createPortal(
      <div className="new-cal-menu-overlay" style={{ zIndex: overlayZIndex }}>
        <div
          className="new-cal-menu-backdrop"
          onClick={() => handleClose(appointmentId)}
          aria-hidden="true"
        />
        <div
          className="new-cal-menu new-cal-menu-portal"
          style={getMenuPortalStyle(rect, variant)}
          onClick={(e) => e.stopPropagation()}
        >
          {getMenuList(apnt)}
        </div>
      </div>,
      document.body,
    );
  };

  const renderCalendarApntCard = (
    timMa,
    listLength,
    widthClass,
    inPopup = false,
    blockClass = "",
    isSpanned = false,
  ) => {
    const apntStatus = getTypeByAppointmentType(timMa?.appointmentStatus);
    const isCancelled = isApntCancelled(timMa);
    const cardWidthClass =
      widthClass ?? (listLength === 1 ? "w100Per" : "w70Per");

    return (
      <div
        className={`cursor ${cardWidthClass} ${inPopup ? "new-cal-apnt-card-in-popup" : ""}`}
        key={`appId-${timMa?.appointmentId}`}
      >
        <div
          className={`${
            isCancelled
              ? "cal-cancelled-box"
              : getClassNameForCalendar(timMa?.serviceType)
          } pos-rel new-cal-apnt-card-menu-wrap${
            isSpanned ? " new-cal-apnt-spanned-card" : ""
          }${
            !isSpanned && blockClass
              ? ` new-cal-slot-apnt-block ${blockClass}`
              : ""
          }`.trim()}
        >
          <div className="flex-row">
            <div className="w90Per" onClick={() => navDetailsPage(timMa)}>
              <div
                className={`flex-column ${inPopup ? "new-cal-apnt-popup-body" : "new-cal-apnt-card-body"}`}
              >
                <div className="flex-row">
                  <Typography
                    className={`font-bold fs10 capitalize ${inPopup ? "new-cal-apnt-text-wrap" : ""}`}
                  >
                    {`${timMa?.petName} & ${timMa?.userName ?? ""}`}
                  </Typography>
                </div>
                <div className="flex-row">
                  <Typography
                    className={`font-bold fs10 capitalize ${inPopup ? "new-cal-apnt-text-wrap" : ""}`}
                  >
                    {timMa?.serviceType ?? ""}
                  </Typography>
                </div>
                <div className="flex-row-ali-cen mv2">
                  <Typography
                    className={`fs8 txt-regular white-color con-fit flex-center upcom-app-box ${timMa?.appointmentType === "Physical" ? "light-blue-back5" : timMa?.appointmentType === "Virtual" ? "rose-back2" : "orange-back3"}`}
                  >
                    {appointmentTypeEnum[timMa?.appointmentType]}
                  </Typography>
                  <Typography
                    className={`ml3 fs8 txt-regular white-color con-fit flex-center upcom-app-box ${getCardColorByApptStatus(
                      timMa?.appointmentStatus?.toLowerCase(),
                    )}`}
                  >
                    {apntStatus}
                  </Typography>
                  <Typography className="ml3 font-bold fs8">
                    {timMa?.startTime ??
                      moment(timMa?.appointmentdateTime).format("HH:mm")}
                  </Typography>
                </div>
                {timMa?.remarks ? (
                  <div className="flex-start mt2 new-cal-apnt-remarks-wrap">
                    <Tooltip title={timMa.remarks} placement="top" arrow>
                      <Typography
                        component="span"
                        className="fs10 txt-regular new-cal-apnt-remarks"
                      >
                        {timMa.remarks}
                      </Typography>
                    </Tooltip>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="w10Per">
              {timMa?.appointmentType === "Virtual" ||
              timMa?.appointmentStatus?.toLowerCase() === "pending" ||
              isCancelled ? null : (
                <MoreVertIcon
                  className="new-cal-apnt-menu-trigger"
                  sx={{
                    height: 20,
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                    ":hover": { color: "#1976d2" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    anchorEls?.[timMa?.appointmentId]
                      ? handleClose(timMa?.appointmentId)
                      : handleClick(
                          timMa?.appointmentId,
                          e,
                          timMa,
                          inPopup ? "popup" : "card",
                        );
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUnavailableSlot = (
    unavailability,
    vet,
    showDetails = true,
    blockClass = "",
  ) => {
    const title = unavailability?.title?.trim();
    const remarks = unavailability?.holliday?.trim();
    const hasTitle = !!title;
    const hasRemarks = !!remarks;
    const canManageUnavail =
      unavailability?.createdBy === (vet?.doctorId ?? vet?.value);

    return (
      <div className="cursor w100Per">
        <div
          className={`cal-cancelled-box w100Per new-cal-slot-unavailable pos-rel new-cal-apnt-card-menu-wrap ${blockClass}`.trim()}
        >
          {showDetails ? (
            <div className="flex-row">
              <div className={canManageUnavail ? "w90Per" : "w100Per"}>
                <div
                  className={`flex-column new-cal-apnt-card-body ${
                    !hasTitle && !hasRemarks
                      ? "new-cal-slot-unavailable-fallback-body"
                      : ""
                  }`}
                >
                  {hasTitle ? (
                    <div className="flex-row">
                      <Typography className="font-bold fs10 capitalize">
                        {title}
                      </Typography>
                    </div>
                  ) : null}
                  {hasRemarks ? (
                    <div className="flex-start mt2 new-cal-apnt-remarks-wrap">
                      <Tooltip title={remarks} placement="top" arrow>
                        <Typography
                          component="span"
                          className="fs10 txt-regular new-cal-apnt-remarks"
                        >
                          {remarks}
                        </Typography>
                      </Tooltip>
                    </div>
                  ) : null}
                  {!hasTitle && !hasRemarks ? (
                    <Typography className="fs10 font-bold gray4 new-cal-slot-unavailable-text">
                      Unavailable
                    </Typography>
                  ) : null}
                </div>
              </div>
              {canManageUnavail ? (
                <div className="w10Per">
                  <MoreVertIcon
                    className="new-cal-apnt-menu-trigger"
                    sx={{
                      height: 20,
                      cursor: "pointer",
                      transition: "color 0.3s ease",
                      ":hover": { color: "#1976d2" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnavailMenuClick(unavailability, vet, e);
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const getVetAppointmentContinuationInfo = (
    appointments,
    slotStart,
    slotSize,
    timeSlots,
  ) => {
    for (const apnt of appointments ?? []) {
      if (!apnt || isApntCancelled(apnt)) continue;

      const apntStart = getApntStartTime(apnt);
      const apntEnd = getApntEndTimeRaw(apnt);
      if (!apntStart || !apntEnd) continue;

      if (
        !doesSlotOverlapAppointmentTime(slotStart, slotSize, apntStart, apntEnd)
      ) {
        continue;
      }

      if (isTimeInSlot(apntStart, slotStart, slotSize)) continue;

      return {
        appointment: apnt,
        blockClass: getAppointmentSlotBlockClass(
          slotStart,
          slotSize,
          apntStart,
          apntEnd,
          timeSlots,
        ),
      };
    }
    return null;
  };

  const renderAppointmentContinuationSlot = (apnt, blockClass = "") => {
    const isCancelled = isApntCancelled(apnt);

    return (
      <div className="new-cal-apnt-continuation-wrap">
        <div
          className={`${
            isCancelled
              ? "cal-cancelled-box"
              : getClassNameForCalendar(apnt?.serviceType)
          } w100Per new-cal-slot-apnt-block pos-rel ${blockClass}`.trim()}
        />
      </div>
    );
  };

  const getSlotAppointmentRowSpan = (
    appointmentsInSlot,
    slotStart,
    slotSize,
    timeSlots,
    allVetAppointments = [],
  ) => {
    let maxRowSpan = 1;

    for (const apnt of appointmentsInSlot ?? []) {
      if (!apnt || isApntCancelled(apnt)) continue;

      const apntStart = getApntStartTime(apnt);
      const apntEnd = getApntEndTimeRaw(apnt);
      if (!apntStart || !apntEnd) continue;

      const overlappingSlots = getOverlappingAppointmentSlots(
        slotSize,
        apntStart,
        apntEnd,
        timeSlots,
      );
      if (overlappingSlots.length <= 1 || overlappingSlots[0] !== slotStart) {
        continue;
      }

      let spanCount = 1;
      for (let i = 1; i < overlappingSlots.length; i++) {
        const nextSlot = overlappingSlots[i];
        const hasOtherStarting = (allVetAppointments ?? []).some((other) => {
          if (!other || isApntCancelled(other)) return false;
          if (other.appointmentId === apnt.appointmentId) return false;
          const otherStart = getApntStartTime(other);
          return otherStart && isTimeInSlot(otherStart, nextSlot, slotSize);
        });
        if (hasOtherStarting) break;
        spanCount = i + 1;
      }

      if (spanCount > maxRowSpan) {
        maxRowSpan = spanCount;
      }
    }

    return maxRowSpan;
  };

  const isSlotCoveredByActiveRowSpan = (
    vetAppointments,
    slotStart,
    slotSize,
    timeSlots,
  ) => {
    const currentIndex = (timeSlots ?? []).indexOf(slotStart);
    if (currentIndex <= 0) return false;

    const groupedByTime = getAppointmentsByTime(vetAppointments) ?? {};

    for (let i = 0; i < currentIndex; i++) {
      const earlierSlot = timeSlots[i];
      const slotKey = getSlotTimeRangeKey(earlierSlot, slotSize);
      const apptsAtSlot = groupedByTime?.[slotKey] ?? [];
      if (!apptsAtSlot.length) continue;

      const rowSpan = getSlotAppointmentRowSpan(
        apptsAtSlot,
        earlierSlot,
        slotSize,
        timeSlots,
        vetAppointments,
      );
      if (rowSpan > 1 && currentIndex < i + rowSpan) {
        return true;
      }
    }

    return false;
  };

  const renderApntCellContent = (
    appointments,
    moreSlotKey,
    vetCount,
    slotStart = null,
    slotSize = 60,
    timeSlots = [],
    rowSpan = 1,
  ) => {
    const { visible, hiddenCount } = getApntCellDisplay(appointments, vetCount);
    const hasMoreLink = hiddenCount > 0;
    const isSpanned = rowSpan > 1;

    return (
      <div
        className={`appointment-box${isSpanned ? " new-cal-apnt-spanned-box" : ""}`}
      >
        <div
          className={`new-cal-apnt-cell-row${isSpanned ? " new-cal-apnt-spanned-row" : ""}`}
        >
          {visible.map((timMa) =>
            renderCalendarApntCard(
              timMa,
              appointments?.length,
              "new-cal-apnt-cell-card",
              false,
              "",
              isSpanned,
            ),
          )}
          {hasMoreLink ? (
            <div className="new-cal-more-apnts-wrapper">
              <div
                className="flex-center font-bold fs10 cursor new-cal-more-apnts-trigger blue-color"
                onClick={(e) => toggleMoreSlot(moreSlotKey, e, appointments)}
              >
                + {hiddenCount} more
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const isDateInHolidayRange = (checkDateStr, holiday) => {
    const startDate = holiday?.date;
    if (!startDate) return false;
    const endDate = holiday?.endDate ?? startDate;
    return checkDateStr >= startDate && checkDateStr <= endDate;
  };

  const checkAvailablityById = (vetDet) => {
    if (!vetDet?.doctorId) return "";

    const checkDateStr =
      selectedType === "week"
        ? moment().format("YYYY-MM-DD")
        : moment(selectedDate).isValid()
          ? moment(selectedDate).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");

    const isClinicHoliday = (Array.isArray(holidays) ? holidays : []).some(
      (holiday) => isDateInHolidayRange(checkDateStr, holiday),
    );
    if (isClinicHoliday) return "ON LEAVE";
    if (vetDet?.actualRole === "ROLE_CLINIC") return "";

    const vetMatch = (
      Array.isArray(clinicVetsUnavailability) ? clinicVetsUnavailability : []
    )?.find((al) => al?.doctorId === vetDet?.doctorId);
    if (!vetMatch) return "ON LEAVE";

    const isOnLeave = (
      Array.isArray(vetMatch?.holliday) ? vetMatch.holliday : []
    )
      ?.filter((hl) => hl?.type === "holliday")
      ?.some((vh) => isDateInHolidayRange(checkDateStr, vh));

    return isOnLeave ? "ON LEAVE" : "";
  };

  const getCalendarCheckDateStr = () => {
    if (selectedType === "today") return moment().format("YYYY-MM-DD");
    return moment(selectedDate).isValid()
      ? moment(selectedDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
  };

  const getVetSlotUnavailability = (
    doctorId,
    slotStart,
    slotSize,
    checkDateStr,
  ) => {
    if (!doctorId) return null;
    const vetMatch = (
      Array.isArray(clinicVetsUnavailability) ? clinicVetsUnavailability : []
    )?.find((al) => al?.doctorId === doctorId);
    if (!vetMatch?.holliday?.length) return null;

    return (
      vetMatch.holliday.find(
        (hl) =>
          hl?.type === "unavailability" &&
          hl?.date === checkDateStr &&
          doesSlotOverlapUnavailability(slotStart, slotSize, hl),
      ) ?? null
    );
  };

  const isVetSlotUnavailable = (doctorId, slotStart, slotSize, checkDateStr) =>
    !!getVetSlotUnavailability(doctorId, slotStart, slotSize, checkDateStr);

  const getFacilitySlotUnavailability = (
    facilityId,
    slotStart,
    slotSize,
    checkDateStr,
  ) => {
    if (!facilityId) return null;
    const facilityMatch = (Array.isArray(facilities) ? facilities : [])?.find(
      (fl) => fl?.facilityId === facilityId,
    );
    if (!facilityMatch?.clinicFacilitiesUnavaility?.length) return null;

    const match = facilityMatch.clinicFacilitiesUnavaility.find((ua) => {
      if (ua?.appointmentDate !== checkDateStr) return false;
      const normalized = {
        ...ua,
        startTime: ua?.appointmentStartTime ?? ua?.startTime,
        endTime: ua?.appointmentEndTime ?? ua?.endTime,
      };
      return doesSlotOverlapUnavailability(slotStart, slotSize, normalized);
    });

    if (!match) return null;
    return {
      ...match,
      startTime: match?.appointmentStartTime ?? match?.startTime,
      endTime: match?.appointmentEndTime ?? match?.endTime,
      title: match?.title,
      holliday: match?.remarks ?? match?.holliday ?? match?.reason,
    };
  };

  const isFacilitySlotUnavailable = (
    facilityId,
    slotStart,
    slotSize,
    checkDateStr,
  ) =>
    !!getFacilitySlotUnavailability(
      facilityId,
      slotStart,
      slotSize,
      checkDateStr,
    );

  const getCalendarTableContainerSx = () => ({
    height: calendarTableHeight ?? "50vh",
    width: "100%",
    overflow: "auto",
  });

  const DayViewCalendar = () => {
    const calendarVetList = getVetListNew(true, false, true) ?? [];
    const todayStr = moment().format("YYYY-MM-DD");
    const isSelectedDateToday =
      selectedType === "today" ||
      (selectedDate &&
        moment(selectedDate).isValid() &&
        moment(selectedDate).format("YYYY-MM-DD") === todayStr);

    return (
      <TableContainer sx={getCalendarTableContainerSx()}>
        <Table
          className="new-cal-day-table"
          sx={{
            width: "100%",
            minWidth: getCalTableMinWidth(calendarVetList.length),
            tableLayout: "fixed",
            "--cal-slot-row-height": `${CAL_SLOT_ROW_HEIGHT}px`,
          }}
          aria-labelledby="tableTitle"
          stickyHeader
        >
          {renderCalColGroup(calendarVetList)}
          <TableHead>
            <TableRow>
              {[{}, ...calendarVetList]?.map((vet, i) => {
                const nextApntTime =
                  i > 0 ? getNextAppointmentTime(vet?.doctorId) : null;
                return (
                  <TableCell
                    key={`vet-${i}-${vet?.value ?? ""}`}
                    style={{
                      ...(i === 0
                        ? {
                            backgroundColor: AppColors.white,
                            color: AppColors.black,
                            textAlign: "left",
                            position: "sticky",
                            left: 0,
                            zIndex: 105,
                            border: ".5px solid #e5e7eb",
                            height: 70,
                          }
                        : {
                            backgroundColor: AppColors.white,
                            color: AppColors.black,
                            textAlign: "center",
                            border: ".5px solid #e5e7eb",
                            height: 70,
                          }),
                      ...getCalColWidthStyle(i),
                    }}
                    sortDirection={false}
                    className={`table-header-text capitalize ${i === 0 ? "new-cal-time-col" : "new-cal-vet-col"}`}
                  >
                    {i === 0 ? (
                      <div style={getCalColInnerStyle(0)}>
                        <div />
                      </div>
                    ) : (
                      <div style={getCalColInnerStyle(i)}>
                        <div
                          className="cursor"
                          onClick={() => onNavVetPage(vet)}
                        >
                          <div className="flex-row-ali-cen flex-center">
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold capitalize fs14">
                                {getRightSaluation(clinicVets, vet?.id) ?? ""}
                                {vet?.name}
                              </Typography>
                            </div>
                            {checkAvailablityById(vet)?.length > 0 ? (
                              <div className="red-back2 fs8 br5 ph10 white-color ml5 h20 flex-center">
                                {checkAvailablityById(vet)}
                              </div>
                            ) : null}
                            {/* <div className="ml5 w30h30 card-con-green-back br20 flex-center">
                            <div className="fs10 white-color">85%</div>
                          </div> */}
                          </div>
                          <Typography className="fs12 font-medium gray4">
                            {vet?.role ?? "Clinic"}
                          </Typography>
                          <Typography className="fs12 font-medium gray4">
                            {getAppointmentsByVet(null, vet?.doctorId)?.length}{" "}
                            appointments
                          </Typography>
                          {nextApntTime ? (
                            <div className="flex-row-ali-cen flex-center">
                              <Typography className="fs12 txt-semi-bold blue-color">
                                Next:
                              </Typography>
                              <Typography className="fs12 txt-semi-bold gray4 ml5">
                                {nextApntTime}
                              </Typography>
                            </div>
                          ) : null}
                          {/* <Typography className="fs12 font-medium gray4 ml5">
                            (5 free slots)
                          </Typography> */}

                          {/* <div className="flex-center">
                                    <Typography className="fs12 font-medium blue-color">
                                      No slots available
                                    </Typography>
                                  </div> */}
                        </div>
                      </div>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeList?.length > 0 &&
              timeList?.map((tl, i) => {
                const currTime = moment().format("HH:mm");
                const slotSize = calFilterValues?.slotSize ?? 60;
                const timeIsBet = isTimeInSlot(currTime, tl, slotSize);
                const highlightCurrentTime = timeIsBet && isSelectedDateToday;
                const slotTimeKey = getSlotTimeRangeKey(tl, slotSize);
                const checkDateStr = getCalendarCheckDateStr();
                return (
                  <TableRow
                    tabIndex={-1}
                    ref={highlightCurrentTime ? timeRef : null}
                    key={`time-${i}-${tl}`}
                    className="new-cal-day-body-row"
                  >
                    <TableCell
                      key={`time-${i}-${tl}-cell`}
                      className="new-cal-time-col new-cal-day-body-cell"
                      style={{
                        backgroundColor: AppColors.white,
                        color: AppColors.black,
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 100,
                        border: ".5px solid #e5e7eb",
                        ...getCalTimeColStyle(),
                      }}
                    >
                      <div style={getCalColInnerStyle(0)}>
                        {
                          <Typography
                            className={`font-bold fs12 ${
                              highlightCurrentTime ? "red-color" : "black"
                            }`}
                          >
                            {tl}
                          </Typography>
                        }
                      </div>
                    </TableCell>
                    {calendarVetList?.length > 0 &&
                      calendarVetList?.map((vt, ind) => {
                        const groupedAppoint = getAppointmentsByVet();
                        const vetApntMat = groupedAppoint?.[vt?.doctorId] ?? [];
                        const matchRecors =
                          vetApntMat?.length > 0
                            ? (getAppointmentsByTime(vetApntMat) ?? [])
                            : [];
                        const vetMatchRec = matchRecors?.[slotTimeKey] ?? [];
                        const moreSlotKey = `${vt?.doctorId}-${slotTimeKey}`;
                        const isSlotUnavailable = isVetSlotUnavailable(
                          vt?.doctorId,
                          tl,
                          slotSize,
                          checkDateStr,
                        );
                        const slotUnavailability = getVetSlotUnavailability(
                          vt?.doctorId,
                          tl,
                          slotSize,
                          checkDateStr,
                        );
                        const showUnavailDetails = slotUnavailability
                          ? isFirstUnavailabilityDisplaySlot(
                              tl,
                              slotSize,
                              slotUnavailability,
                              timeList,
                            )
                          : false;
                        const unavailBlockClass = slotUnavailability
                          ? getUnavailabilitySlotBlockClass(
                              tl,
                              slotSize,
                              slotUnavailability,
                              timeList,
                            )
                          : "";
                        const canBookSlot = canBookAppointmentForVet(vt);

                        if (
                          isSlotCoveredByActiveRowSpan(
                            vetApntMat,
                            tl,
                            slotSize,
                            timeList,
                          )
                        ) {
                          return null;
                        }

                        const rowSpan =
                          vetMatchRec?.length > 0
                            ? getSlotAppointmentRowSpan(
                                vetMatchRec,
                                tl,
                                slotSize,
                                timeList,
                                vetApntMat,
                              )
                            : 1;
                        const appointmentContinuation =
                          vetMatchRec?.length === 0
                            ? getVetAppointmentContinuationInfo(
                                vetApntMat,
                                tl,
                                slotSize,
                                timeList,
                              )
                            : null;
                        const isSlotBlocked =
                          vetMatchRec?.length > 0 ||
                          isSlotUnavailable ||
                          appointmentContinuation ||
                          !canBookSlot;

                        return (
                          <TableCell
                            key={`vet-mat-${ind}-${vt?.value}`}
                            rowSpan={rowSpan > 1 ? rowSpan : undefined}
                            style={{
                              backgroundColor: AppColors.white,
                              color: AppColors.black,
                              textAlign: "center",
                              border: ".5px solid #e5e7eb",
                              padding: 0,
                              ...(rowSpan > 1
                                ? { "--cal-apnt-row-span": String(rowSpan) }
                                : {}),
                              ...getCalColWidthStyle(ind + 1),
                            }}
                            className={`new-cal-vet-col new-cal-day-body-cell${
                              rowSpan > 1 ? " new-cal-apnt-spanned-cell" : ""
                            } ${isSlotBlocked ? "" : "cursor"}`}
                            onClick={() => {
                              if (isSlotBlocked) {
                                return;
                              }
                              onOpenBookApnt(
                                vt,
                                moment().format("YYYY-MM-DD"),
                                tl,
                              );
                            }}
                          >
                            <div className="new-cal-apnt-cell-inner">
                              {vetMatchRec?.length === 0 ? (
                                isSlotUnavailable ? (
                                  renderUnavailableSlot(
                                    slotUnavailability,
                                    vt,
                                    showUnavailDetails,
                                    unavailBlockClass,
                                  )
                                ) : appointmentContinuation ? (
                                  renderAppointmentContinuationSlot(
                                    appointmentContinuation.appointment,
                                    appointmentContinuation.blockClass,
                                  )
                                ) : canBookSlot ? (
                                  <div className="calendar-book-slot">
                                    <Typography className="fs12 blue-color calendar-book-slot-text">
                                      Click to book
                                    </Typography>
                                  </div>
                                ) : null
                              ) : (
                                renderApntCellContent(
                                  vetMatchRec,
                                  moreSlotKey,
                                  calendarVetList?.length,
                                  tl,
                                  slotSize,
                                  timeList,
                                  rowSpan,
                                )
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const getDateBasedRecords = () => {
    const listToFilter =
      (selectedVet?.value === "all" ? appointmentLists : vetAppointments) ?? [];
    const reqList = groupBy(listToFilter, "appointmentDate");
    return reqList;
  };

  const WeekViewCalendar = () => {
    const calendarVetList = getVetListNew(true, false, true) ?? [];

    return (
      <TableContainer sx={getCalendarTableContainerSx()}>
        <Table
          className="new-cal-week-table"
          sx={{
            width: "100%",
            minWidth: getCalTableMinWidth(calendarVetList.length),
            tableLayout: "fixed",
            "--cal-slot-row-height": `${CAL_SLOT_ROW_HEIGHT}px`,
          }}
          aria-labelledby="tableTitle"
          stickyHeader
        >
          {renderCalColGroup(calendarVetList)}
          <TableHead>
            <TableRow>
              {[{}, ...calendarVetList]?.map((vet, i) => (
                <TableCell
                  key={`vet-${i}-${vet?.value ?? ""}`}
                  style={getCalWeekHeaderCellStyle(i)}
                  sortDirection={false}
                  className={`new-cal-week-header-cell table-header-text capitalize ${i === 0 ? "new-cal-time-col" : "new-cal-vet-col"}`}
                >
                  {i === 0 ? (
                    <div style={getCalColInnerStyle(0)}>
                      <div />
                    </div>
                  ) : (
                    <div
                      style={{ ...getCalColInnerStyle(i), lineHeight: 1.15 }}
                    >
                      <div className="cursor" onClick={() => onNavVetPage(vet)}>
                        <div className="flex-row-ali-cen flex-center">
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold capitalize fs14">
                              {getRightSaluation(clinicVets, vet?.id)}
                              {vet?.name}
                            </Typography>
                          </div>
                          {checkAvailablityById(vet) ? (
                            <div className="red-back2 fs8 br5 ph10 white-color ml5 h20 flex-center">
                              {checkAvailablityById(vet)}
                            </div>
                          ) : null}
                          {/* <div className="ml5 w30h30 card-con-green-back br20 flex-center">
                            <div className="fs10 white-color">85%</div>
                          </div> */}
                        </div>
                        <Typography className="fs12 font-medium gray4">
                          {vet?.role ?? "Clinic"}
                        </Typography>
                        <Typography className="fs12 font-medium gray4">
                          {getAppointmentsByVet(null, vet?.doctorId)?.length}{" "}
                          appointments
                        </Typography>

                        {/* <div className="flex-center">
                                    <Typography className="fs12 font-medium blue-color">
                                      No slots available
                                    </Typography>
                                  </div> */}
                      </div>
                    </div>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dateList?.length > 0 &&
              dateList?.map((dl, i) => {
                const reqDate = moment(dl).format("YYYY-MM-DD");
                const isTodayRow = reqDate === moment().format("YYYY-MM-DD");
                const dayList = getAppointmentsByDate()?.[reqDate] ?? [];
                const isLastWeekRow = i === dateList.length - 1;
                return (
                  <TableRow
                    tabIndex={-1}
                    key={i}
                    className={`new-cal-week-body-row ${isLastWeekRow ? "new-cal-week-last-body-row" : ""}`}
                  >
                    <TableCell
                      key={`all-week-${dl}-${i}`}
                      className="new-cal-time-col new-cal-week-body-cell"
                      style={{
                        backgroundColor: AppColors.white,
                        color: AppColors.black,
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 100,
                        border: ".5px solid #e5e7eb",
                        ...getCalTimeColStyle(),
                      }}
                    >
                      <div style={getCalColInnerStyle(0)}>
                        <Typography
                          className={`font-bold fs12 ${
                            isTodayRow ? "red-color" : "black"
                          }`}
                        >
                          {moment(dl).format("ddd DD")}
                        </Typography>
                      </div>
                    </TableCell>
                    {calendarVetList?.length > 0 &&
                      calendarVetList?.map((vet, i) => {
                        const groupedAppoint = getAppointmentsByVet(dayList);
                        const vetApntMat =
                          groupedAppoint?.[vet?.doctorId] ?? [];
                        const matchRecors =
                          vetApntMat?.length > 0
                            ? (getAppointmentsByTime(vetApntMat) ?? [])
                            : [];
                        const dayData = vetApntMat ?? [];
                        const moreSlotKey = `${vet?.doctorId}-${reqDate}`;
                        const canBookSlot = canBookAppointmentForVet(vet);

                        return (
                          <TableCell
                            key={`all-week-${vet?.value ?? ""}-${i}`}
                            style={{
                              backgroundColor: AppColors.white,
                              color: AppColors.black,
                              textAlign: "center",
                              border: ".5px solid #e5e7eb",
                              padding: 0,
                              ...getCalColWidthStyle(i + 1),
                            }}
                            className={`new-cal-vet-col new-cal-week-body-cell ${
                              dayData?.length > 0 || !canBookSlot
                                ? ""
                                : "cursor"
                            }`}
                            onClick={() => {
                              if (dayData?.length > 0 || !canBookSlot) return;
                              onOpenBookApnt(
                                vet,
                                reqDate,
                                moment().format("HH:mm"),
                              );
                            }}
                          >
                            <div className="new-cal-apnt-cell-inner new-cal-week-body-inner">
                              {dayData?.length === 0 ? (
                                canBookSlot ? (
                                  <div className="calendar-book-slot new-cal-week-book-slot">
                                    <Typography className="fs12 blue-color calendar-book-slot-text">
                                      Click to book
                                    </Typography>
                                  </div>
                                ) : null
                              ) : (
                                renderApntCellContent(
                                  dayData,
                                  moreSlotKey,
                                  calendarVetList?.length,
                                )
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const DayViewFacilityCalendar = () => {
    const calendarFacilityList = getFacilityList() ?? [];
    const todayStr = moment().format("YYYY-MM-DD");
    const isSelectedDateToday =
      selectedType === "today" ||
      (selectedDate &&
        moment(selectedDate).isValid() &&
        moment(selectedDate).format("YYYY-MM-DD") === todayStr);
    const bookDateStr = getCalendarCheckDateStr();

    return (
      <TableContainer sx={getCalendarTableContainerSx()}>
        <Table
          className="new-cal-day-table"
          sx={{
            width: "100%",
            minWidth: getCalTableMinWidth(calendarFacilityList.length),
            tableLayout: "fixed",
            "--cal-slot-row-height": `${CAL_SLOT_ROW_HEIGHT}px`,
          }}
          aria-labelledby="tableTitle"
          stickyHeader
        >
          {renderCalColGroup(calendarFacilityList)}
          <TableHead>
            <TableRow>
              {[{}, ...calendarFacilityList]?.map((facility, i) => {
                const nextApntTime =
                  i > 0
                    ? getNextFacilityAppointmentTime(facility?.facilityId)
                    : null;
                return (
                  <TableCell
                    key={`facility-${i}-${facility?.value ?? ""}`}
                    style={{
                      ...(i === 0
                        ? {
                            backgroundColor: AppColors.white,
                            color: AppColors.black,
                            textAlign: "left",
                            position: "sticky",
                            left: 0,
                            zIndex: 105,
                            border: ".5px solid #e5e7eb",
                            height: 70,
                          }
                        : {
                            backgroundColor: AppColors.white,
                            color: AppColors.black,
                            textAlign: "center",
                            border: ".5px solid #e5e7eb",
                            height: 70,
                          }),
                      ...getCalColWidthStyle(i),
                    }}
                    sortDirection={false}
                    className={`table-header-text capitalize ${i === 0 ? "new-cal-time-col" : "new-cal-vet-col"}`}
                  >
                    {i === 0 ? (
                      <div style={getCalColInnerStyle(0)}>
                        <div />
                      </div>
                    ) : (
                      <div style={getCalColInnerStyle(i)}>
                        <div>
                          <div className="flex-row-ali-cen flex-center">
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold capitalize fs14">
                                {facility?.name}
                              </Typography>
                            </div>
                          </div>
                          <Typography className="fs12 font-medium gray4 light-blue-back6 con-fit br5 ph10 blue-color">
                            {facility?.role ?? "Facility"}
                          </Typography>
                          <Typography className="fs12 font-medium gray4">
                            {
                              getAppointmentsByFacility(
                                null,
                                facility?.facilityId,
                              )?.length
                            }{" "}
                            appointments
                          </Typography>
                          {nextApntTime ? (
                            <div className="flex-row-ali-cen flex-center">
                              <Typography className="fs12 txt-semi-bold blue-color">
                                Next:
                              </Typography>
                              <Typography className="fs12 txt-semi-bold gray4 ml5">
                                {nextApntTime}
                              </Typography>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeList?.length > 0 &&
              timeList?.map((tl, i) => {
                const currTime = moment().format("HH:mm");
                const slotSize = calFilterValues?.slotSize ?? 60;
                const timeIsBet = isTimeInSlot(currTime, tl, slotSize);
                const highlightCurrentTime = timeIsBet && isSelectedDateToday;
                const slotTimeKey = getSlotTimeRangeKey(tl, slotSize);
                const checkDateStr = bookDateStr;
                return (
                  <TableRow
                    tabIndex={-1}
                    ref={highlightCurrentTime ? timeRef : null}
                    key={`facility-time-${i}-${tl}`}
                    className="new-cal-day-body-row"
                  >
                    <TableCell
                      key={`facility-time-${i}-${tl}-cell`}
                      className="new-cal-time-col new-cal-day-body-cell"
                      style={{
                        backgroundColor: AppColors.white,
                        color: AppColors.black,
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 100,
                        border: ".5px solid #e5e7eb",
                        ...getCalTimeColStyle(),
                      }}
                    >
                      <div style={getCalColInnerStyle(0)}>
                        {
                          <Typography
                            className={`font-bold fs12 ${
                              highlightCurrentTime ? "red-color" : "black"
                            }`}
                          >
                            {tl}
                          </Typography>
                        }
                      </div>
                    </TableCell>
                    {calendarFacilityList?.length > 0 &&
                      calendarFacilityList?.map((fl, ind) => {
                        const groupedAppoint = getAppointmentsByFacility();
                        const facilityApntMat =
                          groupedAppoint?.[fl?.facilityId] ??
                          groupedAppoint?.[String(fl?.facilityId)] ??
                          [];
                        const matchRecors =
                          facilityApntMat?.length > 0
                            ? (getAppointmentsByTime(facilityApntMat) ?? [])
                            : [];
                        const facilityMatchRec =
                          matchRecors?.[slotTimeKey] ?? [];
                        const moreSlotKey = `${fl?.facilityId}-${slotTimeKey}`;
                        const isSlotUnavailable = isFacilitySlotUnavailable(
                          fl?.facilityId,
                          tl,
                          slotSize,
                          checkDateStr,
                        );
                        const slotUnavailability =
                          getFacilitySlotUnavailability(
                            fl?.facilityId,
                            tl,
                            slotSize,
                            checkDateStr,
                          );
                        const showUnavailDetails = slotUnavailability
                          ? isFirstUnavailabilityDisplaySlot(
                              tl,
                              slotSize,
                              slotUnavailability,
                              timeList,
                            )
                          : false;
                        const unavailBlockClass = slotUnavailability
                          ? getUnavailabilitySlotBlockClass(
                              tl,
                              slotSize,
                              slotUnavailability,
                              timeList,
                            )
                          : "";

                        if (
                          isSlotCoveredByActiveRowSpan(
                            facilityApntMat,
                            tl,
                            slotSize,
                            timeList,
                          )
                        ) {
                          return null;
                        }

                        const rowSpan =
                          facilityMatchRec?.length > 0
                            ? getSlotAppointmentRowSpan(
                                facilityMatchRec,
                                tl,
                                slotSize,
                                timeList,
                                facilityApntMat,
                              )
                            : 1;
                        const appointmentContinuation =
                          facilityMatchRec?.length === 0
                            ? getVetAppointmentContinuationInfo(
                                facilityApntMat,
                                tl,
                                slotSize,
                                timeList,
                              )
                            : null;
                        const isSlotBlocked =
                          facilityMatchRec?.length > 0 ||
                          isSlotUnavailable ||
                          appointmentContinuation;

                        return (
                          <TableCell
                            key={`facility-mat-${ind}-${fl?.value}`}
                            rowSpan={rowSpan > 1 ? rowSpan : undefined}
                            style={{
                              backgroundColor: AppColors.white,
                              color: AppColors.black,
                              textAlign: "center",
                              border: ".5px solid #e5e7eb",
                              padding: 0,
                              ...(rowSpan > 1
                                ? { "--cal-apnt-row-span": String(rowSpan) }
                                : {}),
                              ...getCalColWidthStyle(ind + 1),
                            }}
                            className={`new-cal-vet-col new-cal-day-body-cell${
                              rowSpan > 1 ? " new-cal-apnt-spanned-cell" : ""
                            } ${isSlotBlocked ? "" : "cursor"}`}
                            onClick={() => {
                              if (isSlotBlocked) {
                                return;
                              }
                              onOpenBookApntForFacility(fl, bookDateStr, tl);
                            }}
                          >
                            <div className="new-cal-apnt-cell-inner">
                              {facilityMatchRec?.length === 0 ? (
                                isSlotUnavailable ? (
                                  renderUnavailableSlot(
                                    slotUnavailability,
                                    fl,
                                    showUnavailDetails,
                                    unavailBlockClass,
                                  )
                                ) : appointmentContinuation ? (
                                  renderAppointmentContinuationSlot(
                                    appointmentContinuation.appointment,
                                    appointmentContinuation.blockClass,
                                  )
                                ) : (
                                  <div className="calendar-book-slot">
                                    <Typography className="fs12 blue-color calendar-book-slot-text">
                                      Click to book
                                    </Typography>
                                  </div>
                                )
                              ) : (
                                renderApntCellContent(
                                  facilityMatchRec,
                                  moreSlotKey,
                                  calendarFacilityList?.length,
                                  tl,
                                  slotSize,
                                  timeList,
                                  rowSpan,
                                )
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const WeekViewFacilityCalendar = () => {
    const calendarFacilityList = getFacilityList() ?? [];

    return (
      <TableContainer sx={getCalendarTableContainerSx()}>
        <Table
          className="new-cal-week-table"
          sx={{
            width: "100%",
            minWidth: getCalTableMinWidth(calendarFacilityList.length),
            tableLayout: "fixed",
            "--cal-slot-row-height": `${CAL_SLOT_ROW_HEIGHT}px`,
          }}
          aria-labelledby="tableTitle"
          stickyHeader
        >
          {renderCalColGroup(calendarFacilityList)}
          <TableHead>
            <TableRow>
              {[{}, ...calendarFacilityList]?.map((facility, i) => (
                <TableCell
                  key={`facility-week-${i}-${facility?.value ?? ""}`}
                  style={getCalWeekHeaderCellStyle(i)}
                  sortDirection={false}
                  className={`new-cal-week-header-cell table-header-text capitalize ${i === 0 ? "new-cal-time-col" : "new-cal-vet-col"}`}
                >
                  {i === 0 ? (
                    <div style={getCalColInnerStyle(0)}>
                      <div />
                    </div>
                  ) : (
                    <div
                      style={{ ...getCalColInnerStyle(i), lineHeight: 1.15 }}
                    >
                      <div>
                        <div className="flex-row-ali-cen flex-center">
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold capitalize fs14">
                              {facility?.name}
                            </Typography>
                          </div>
                        </div>
                        <Typography className="fs12 font-medium light-blue-back6 con-fit br5 ph10 blue-color">
                          {facility?.role ?? "Facility"}
                        </Typography>
                        <Typography className="fs12 font-medium gray4">
                          {
                            getAppointmentsByFacility(
                              null,
                              facility?.facilityId,
                            )?.length
                          }{" "}
                          appointments
                        </Typography>
                      </div>
                    </div>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dateList?.length > 0 &&
              dateList?.map((dl, i) => {
                const reqDate = moment(dl).format("YYYY-MM-DD");
                const isTodayRow = reqDate === moment().format("YYYY-MM-DD");
                const dayList = getAppointmentsByDate()?.[reqDate] ?? [];
                const isLastWeekRow = i === dateList.length - 1;
                return (
                  <TableRow
                    tabIndex={-1}
                    key={`facility-week-row-${i}`}
                    className={`new-cal-week-body-row ${isLastWeekRow ? "new-cal-week-last-body-row" : ""}`}
                  >
                    <TableCell
                      key={`facility-week-${dl}-${i}`}
                      className="new-cal-time-col new-cal-week-body-cell"
                      style={{
                        backgroundColor: AppColors.white,
                        color: AppColors.black,
                        textAlign: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 100,
                        border: ".5px solid #e5e7eb",
                        ...getCalTimeColStyle(),
                      }}
                    >
                      <div style={getCalColInnerStyle(0)}>
                        <Typography
                          className={`font-bold fs12 ${
                            isTodayRow ? "red-color" : "black"
                          }`}
                        >
                          {moment(dl).format("ddd DD")}
                        </Typography>
                      </div>
                    </TableCell>
                    {calendarFacilityList?.length > 0 &&
                      calendarFacilityList?.map((facility, fi) => {
                        const groupedAppoint =
                          getAppointmentsByFacility(dayList);
                        const facilityApntMat =
                          groupedAppoint?.[facility?.facilityId] ??
                          groupedAppoint?.[String(facility?.facilityId)] ??
                          [];
                        const dayData = facilityApntMat ?? [];
                        const moreSlotKey = `${facility?.facilityId}-${reqDate}`;

                        return (
                          <TableCell
                            key={`facility-week-${facility?.value ?? ""}-${fi}`}
                            style={{
                              backgroundColor: AppColors.white,
                              color: AppColors.black,
                              textAlign: "center",
                              border: ".5px solid #e5e7eb",
                              padding: 0,
                              ...getCalColWidthStyle(fi + 1),
                            }}
                            className={`new-cal-vet-col new-cal-week-body-cell ${
                              dayData?.length > 0 ? "" : "cursor"
                            }`}
                            onClick={() => {
                              if (dayData?.length > 0) return;
                              onOpenBookApntForFacility(
                                facility,
                                reqDate,
                                moment().format("HH:mm"),
                              );
                            }}
                          >
                            <div className="new-cal-apnt-cell-inner new-cal-week-body-inner">
                              {dayData?.length === 0 ? (
                                <div className="calendar-book-slot new-cal-week-book-slot">
                                  <Typography className="fs12 blue-color calendar-book-slot-text">
                                    Click to book
                                  </Typography>
                                </div>
                              ) : (
                                renderApntCellContent(
                                  dayData,
                                  moreSlotKey,
                                  calendarFacilityList?.length,
                                )
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    };
    setSearchValues(reqObj);
  };

  const getStatusListByConsultationType = (conType = []) => {
    if (!conType?.length || conType?.length === 0) return [];
    let reqList = [];
    if (conType?.some((li) => li?.value === "Physical")) {
      reqList = [...reqList, ...physicalStatusList];
    }
    if (conType?.some((li) => li?.value === "Virtual")) {
      reqList = [...reqList, ...virtualStatusList];
    }
    if (conType?.some((li) => li?.value === "Home")) {
      reqList = [...reqList, ...homeVisitStatusList];
    }
    const uniqueList =
      Array.from(
        new Map(reqList?.map((item) => [item?.value, item])).values(),
      ) ?? [];
    return [
      { name: "All", value: "All" },
      ...(uniqueList ?? []),
      { name: "Cancelled", value: "Cancelled" },
    ];
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
  };

  const handleChangePage = (e, selectedPage) => {
    onCalendarApiCall(selectedPage);
  };

  const getTableFilteredList = () => {
    const statVal = slctedApntStatus?.value;
    const reqList =
      (selectedVet?.value === "all"
        ? layoutMode === LAYOUT_LIST
          ? appointmentsByFilter?.data
          : appointmentLists
        : layoutMode === LAYOUT_LIST
          ? appointmentsByFilter?.data
          : vetAppointments
      )?.filter((li) => {
        const statLower =
          li?.appointmentStatus?.toLowerCase() ??
          li?.appoinment?.appointmentStatus?.toLowerCase();
        const searchMatch =
          srchValue?.length > 0
            ? (li?.petName ?? li?.appoinment?.petName)
                ?.toLowerCase()
                ?.includes(srchValue?.toLowerCase()) ||
              (li?.appointmentType ?? li?.appoinment?.appointmentType)
                ?.toLowerCase()
                ?.includes(srchValue?.toLowerCase()) ||
              (li?.serviceType ?? li?.appoinment?.serviceType)
                ?.toLowerCase()
                ?.includes(srchValue?.toLowerCase()) ||
              (li?.doctorName ?? li?.appoinment?.doctorName)
                ?.toLowerCase()
                ?.includes(srchValue?.toLowerCase())
            : true;
        const statusMat =
          statVal !== "All"
            ? (statVal === "Upcoming" && statLower === "scheduled") ||
              (statVal === "Checked-in" && statLower === "inprogress") ||
              (statVal === "In Transit" && statLower === "intransit") ||
              (statLower === "consultation" && statVal === "Consultation") ||
              (statVal === "Billing" && statLower === "finalize") ||
              (statVal === "Completed" && statLower === "completed") ||
              (statVal === "Cancelled" &&
                (statLower === "canceled" || statLower === "cancelled"))
            : true;
        const conTypMatch = conTypes?.includes(
          li?.appointmentType ?? li?.appoinment?.appointmentType,
        );
        const serMatch = sleServiceTypes?.includes("All")
          ? true
          : sleServiceTypes?.includes(li?.serviceType);
        return searchMatch && statusMat && conTypMatch && serMatch;
      }) ?? [];
    return reqList?.map((li) => ({ ...li, ...(li?.appoinment ?? {}) })) ?? [];
  };

  const onCloseUpdateWeight = (typ) => {
    setUpdateWeightVsble(false);
    setModValues(null);
  };

  const onFilterReset = () => {
    const reqObj = { ...initFilter, visible: true };
    setFilterValues(reqObj);
  };

  const onApplyFilter = () => {
    onCalendarApiCall(1);
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const getSuggestionList = () => {
    const reqList =
      appointmentsForSuggestion?.data
        ?.map((li) => li?.userName ?? "")
        ?.filter(Boolean) ?? [];
    const uniqList = uniq(reqList ?? []);
    return uniqList;
  };

  const onChangeCalValue = (name, value) => {
    const reqObj = { ...calFilterValues, [name]: value };
    if (name === "visible" && value) {
      setTempFilterValues({ ...reqObj });
    }
    setCalFilterValues(reqObj);
  };

  const onChangeTempValue = (name, value) => {
    const reqObj = { ...tempFilterValues, [name]: value };
    if (name === "roles") reqObj.vets = [{ name: "All", value: "All" }];
    if (
      name === "serviceType" ||
      name === "vets" ||
      name === "appointmentStatus" ||
      name === "facilityTypes"
    ) {
      const isAllOption = (li) =>
        (li?.value ?? "")?.toString()?.toLowerCase() === "all";
      const prevValue = Array.isArray(tempFilterValues?.[name])
        ? tempFilterValues[name]
        : [];
      const newValue = Array.isArray(value) ? value : [];
      const prevHadAll = prevValue.some(isAllOption);
      const newHasAll = newValue.some(isAllOption);
      const newNonAll = newValue.filter((li) => !isAllOption(li));

      if (newHasAll && !prevHadAll) {
        reqObj[name] = [{ name: "All", value: "All" }];
      } else if (newHasAll && prevHadAll && newNonAll.length > 0) {
        reqObj[name] = newNonAll;
      } else if (newHasAll) {
        reqObj[name] = [{ name: "All", value: "All" }];
      } else {
        reqObj[name] = newValue;
      }
    }
    setTempFilterValues(reqObj);
  };

  const onMainFilterApply = (isReset = false) => {
    if (isReset) {
      setTempFilterValues(initCalFilter);
      return setCalFilterValues(initCalFilter);
    }
    if (tempFilterValues?.slotSize !== calFilterValues?.slotSize) {
      getDateAndTimeLists(tempFilterValues?.slotSize, false);
    }
    setCalFilterValues({ ...tempFilterValues, visible: false });
    setTempFilterValues(initCalFilter);
  };

  return (
    <>
      <div className="ph15 mh10 mt5" ref={calendarToolbarRef}>
        {layoutMode === LAYOUT_GRID ? (
          <div className="back-white br8brblack p10 mv5">
            <div className="flex-row">
              <div className="w30Per">
                <div className="flex-row-ali-cen flex-row-wrap">
                  <div className="flex-row-ali-cen">
                    <CalToggleBtn
                      active={selectedType === "date"}
                      onClick={handleSelectDay}
                    >
                      Day
                    </CalToggleBtn>
                    <CalToggleBtn
                      active={selectedType === "week"}
                      onClick={handleSelectWeek}
                    >
                      Week
                    </CalToggleBtn>
                  </div>
                  <div className="ml10">
                    <CalToggleBtn
                      active={selectedType === "today"}
                      onClick={handleSelectToday}
                    >
                      Today
                    </CalToggleBtn>
                  </div>
                </div>
              </div>
              <div className="w40Per">
                <div className="flex-center">
                  <div className="flex-row-ali-cen">
                    <NavigateBeforeOutlinedIcon
                      className={`cal-arrow ${selectedType === "today" ? "no-cursor" : "cursor"}`}
                      onClick={handlePrev}
                    />
                    {selectedType === "date" ? (
                      <div
                        className="mh10 cal-date-label-picker"
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <Typography className="text-bold day-type-box black ph10">
                          {dateLabel}
                        </Typography>
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                        >
                          <CustomTextField
                            mobileDate
                            name="selectedDate"
                            value={selectedDate}
                            handleChange={onChangeSelectedDate}
                            fullWidth
                          />
                        </div>
                      </div>
                    ) : (
                      <Typography className="text-bold mh10 day-type-box black ph10">
                        {dateLabel}
                      </Typography>
                    )}
                    <NavigateNextOutlinedIcon
                      className={`cal-arrow ${selectedType === "today" ? "no-cursor" : "cursor"}`}
                      onClick={handleNext}
                    />
                  </div>
                </div>
              </div>
              <div className="w30Per">
                <div className="flex-end">
                  <div className="flex-row-ali-cen">
                    <div className="flex-row-ali-cen">
                      <CalToggleBtn
                        active={perspective === PERSPECTIVE_VET}
                        onClick={() => setPerspective(PERSPECTIVE_VET)}
                      >
                        Vet View
                      </CalToggleBtn>
                      <CalToggleBtn
                        active={perspective === PERSPECTIVE_FACILITY}
                        onClick={() => setPerspective(PERSPECTIVE_FACILITY)}
                      >
                        Facility View
                      </CalToggleBtn>
                    </div>

                    <Typography
                      className="cursor flex-row-ali-cen mh10 day-type-box2 pv4ph10"
                      onClick={() => onChangeCalValue("visible", true)}
                    >
                      <TuneIcon
                        sx={{
                          width: 18,
                          height: 18,
                          color: AppColors.appPrimary,
                        }}
                      />
                      <Typography className="font-medium fs12 blue-color ml10">
                        Filter
                      </Typography>
                    </Typography>

                    <div className="flex-row-ali-cen">
                      <div
                        className={`cursor cal-tab-icon day-type-box ${
                          layoutMode === LAYOUT_GRID ? "blue-back-con" : ""
                        }`}
                        onClick={() => setLayoutMode(LAYOUT_GRID)}
                      >
                        <DashboardIcon
                          sx={{
                            height: 20,
                            width: 20,
                            color:
                              layoutMode === LAYOUT_GRID
                                ? AppColors.white
                                : AppColors.gray,
                          }}
                        />
                      </div>
                      <div
                        className={`cursor cal-tab-icon day-type-box ${
                          layoutMode === LAYOUT_LIST ? "blue-back-con" : ""
                        }`}
                        onClick={() => setLayoutMode(LAYOUT_LIST)}
                      >
                        <TableRowsIcon
                          sx={{
                            height: 20,
                            width: 20,
                            color:
                              layoutMode === LAYOUT_LIST
                                ? AppColors.white
                                : AppColors.gray,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {layoutMode === LAYOUT_GRID ? (
          calFilterValues?.showStats ? (
            <div className="flex-row-between mv5">
              <CalSummaryCard
                icon={
                  <CalendarMonthIcon
                    sx={{ color: AppColors.appPrimary, fontSize: 20 }}
                  />
                }
                iconBgClass="light-blue-back"
                title="Today's Appointments"
                value={SUMMARY_STATS.todayTotal}
                footer={`${SUMMARY_STATS.todayCompleted}/${SUMMARY_STATS.todayTotal} appointments completed (${completionPercent}%)`}
                progressPercent={completionPercent}
              />
              <CalSummaryCard
                icon={
                  <HourglassEmptyIcon
                    sx={{ color: AppColors.appYellow, fontSize: 20 }}
                  />
                }
                iconBgClass="yel-back"
                title="Pending Requests"
                value={SUMMARY_STATS.pendingRequests}
              />
              <CalSummaryCard
                icon={
                  <GroupsIcon sx={{ color: AppColors.green2, fontSize: 20 }} />
                }
                iconBgClass="grn-bck"
                title="On Duty"
                value={SUMMARY_STATS.onDuty}
                footer={`${SUMMARY_STATS.onLeave} on leave`}
              />
            </div>
          ) : null
        ) : null}
      </div>

      {layoutMode === LAYOUT_GRID ? (
        <div className="back-white ph20 pb10" ref={calendarTableAnchorRef}>
          {selectedType !== "week"
            ? perspective === PERSPECTIVE_FACILITY
              ? DayViewFacilityCalendar()
              : DayViewCalendar()
            : perspective === PERSPECTIVE_FACILITY
              ? WeekViewFacilityCalendar()
              : WeekViewCalendar()}
        </div>
      ) : null}

      {layoutMode === LAYOUT_LIST ? (
        <>
          <Grid container sx={{ padding: 0, alignItems: "center" }}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Container maxWidth="xl">
                {/* <Toolbar variant="regular"> */}
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="flex-row mt15">
                    <div className="top-row-cen-con w100Per">
                      <div className="w15Per">
                        <CustomTextField
                          fullWidth
                          placeholder="Pet Name"
                          name="petName"
                          backgroundColor={AppColors.lightBlue2}
                          value={searchValues?.petName}
                          handleChange={onSearchChange}
                          label="Pet Name"
                        />
                      </div>
                      <div className="w15Per ml20">
                        <CustomSelect
                          list={vetDropList}
                          value={searchValues?.selectedVet}
                          handleChange={(e, value) =>
                            onSearchChange({
                              target: {
                                name: "selectedVet",
                                value: e?.target?.value,
                              },
                            })
                          }
                          // search
                          backgroundColor={AppColors.lightBlue2}
                          multiSelectSearchCheck
                        />
                      </div>
                      {/* <div className="w15Per ml20">
                            <CustomTextField
                              fullWidth
                              placeholder="Vet Name"
                              name="vetName"
                              backgroundColor={AppColors.lightBlue2}
                              value={searchValues?.vetName}
                              handleChange={onSearchChange}
                              label="Vet Name"
                            />
                          </div> */}
                      <div className="w15Per ml20">
                        <CustomSelect
                          list={typeList}
                          value={searchValues?.consultationType}
                          handleChange={onSearchChange}
                          name="consultationType"
                          label="Consultation Type"
                          multiSelectSearchCheck
                          backgroundColor={AppColors.lightBlue2}
                        />
                      </div>
                      <div className="w15Per ml20">
                        <CustomSelect
                          list={getStatusListByConsultationType(
                            searchValues?.consultationType,
                          )}
                          value={searchValues?.status}
                          handleChange={onSearchChange}
                          name="status"
                          label="Status"
                          multiSelectSearchCheck
                          backgroundColor={AppColors.lightBlue2}
                        />
                      </div>
                      <div
                        className="flex-row-ali-cen ml20 filter cursor"
                        onClick={() => onFilterChange("visible", true)}
                      >
                        <TuneIcon sx={{ color: AppColors.appPrimary }} />
                        <Typography className="font-medium fs12 blue-color ml10">
                          {filterValues?.selectedList?.length > 0
                            ? `${filterValues?.selectedList?.length} `
                            : ""}
                          Filter
                        </Typography>
                      </div>
                      <div className="ml20">
                        <CustomButton
                          text="Reset"
                          onClick={onReset}
                          smallBtn
                          redBtn
                        />
                      </div>
                      <div className="flex-row-ali-cen ml20">
                        <div
                          className={`cursor cal-tab-icon day-type-box ${
                            layoutMode === LAYOUT_GRID ? "blue-back-con" : ""
                          }`}
                          onClick={() => setLayoutMode(LAYOUT_GRID)}
                        >
                          <DashboardIcon
                            sx={{
                              height: 20,
                              width: 20,
                              color:
                                layoutMode === LAYOUT_GRID
                                  ? AppColors.white
                                  : AppColors.gray,
                            }}
                          />
                        </div>
                        <div
                          className={`cursor cal-tab-icon day-type-box ${
                            layoutMode === LAYOUT_LIST ? "blue-back-con" : ""
                          }`}
                          onClick={() => setLayoutMode(LAYOUT_LIST)}
                        >
                          <TableRowsIcon
                            sx={{
                              height: 20,
                              width: 20,
                              color:
                                layoutMode === LAYOUT_LIST
                                  ? AppColors.white
                                  : AppColors.gray,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Typography className="font-medium fs12 gray28 mv10">
                    Number of Records: {appointmentsByFilter?.totalRecords ?? 0}
                  </Typography>
                </Grid>
                {/* </Toolbar> */}

                <TableContainer sx={{ maxHeight: 450 }}>
                  <Table
                    sx={{ minWidth: 200 }}
                    aria-labelledby="tableTitle"
                    stickyHeader
                  >
                    <TableHead>
                      <TableRow>
                        {tblHdrs?.map((hdr, i) => (
                          <TableCell
                            key={`${hdr}-${i}-tblVi`}
                            sortDirection={false}
                            // style={{
                            //   background: AppColors.tableGrey,
                            //   color: AppColors.white,
                            // }}
                            style={tblGrayBack}
                            className="txt-semi-bold fs12 capitalize"
                          >
                            {nameExpan?.[hdr]}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    {getTableFilteredList()?.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={12}
                          className="text-align-center h100"
                        >
                          <div className="no-rec">No records available</div>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableBody>
                      {getTableFilteredList().map((li, ind) => {
                        return (
                          <>
                            <TableRow key={`${li?.appointmentId}-${ind}`}>
                              {tblHdrs?.map((hdr, ind) => (
                                <TableCell
                                  key={ind + `${ind}` + "tr"}
                                  component="th"
                                >
                                  {hdr === "petName" ? (
                                    <div onClick={() => navDetailsPage(li)}>
                                      <Custom3dotString
                                        str={li?.[hdr]}
                                        className="font-bold fs14 capitalize cursor blue-color underline"
                                      />
                                    </div>
                                  ) : hdr === "appointmentType" ? (
                                    <Typography className="font-medium fs14 black">
                                      {li?.[hdr] === "Home"
                                        ? "Home Visit"
                                        : li?.[hdr]}
                                    </Typography>
                                  ) : hdr === "vetName" ? (
                                    <Typography className="font-medium fs14 black">
                                      {li?.doctorName}
                                    </Typography>
                                  ) : hdr === "apntDate" ? (
                                    <Typography className="font-medium fs14 black">
                                      {moment(li?.appointmentdateTime).format(
                                        "DD MMM YYYY, HH:mm",
                                      )}
                                    </Typography>
                                  ) : hdr === "status" ? (
                                    <Typography
                                      className={`fs12 txt-regular white-color flex-center ph10 pv5 br5 ${
                                        li?.appointmentStatus?.toLowerCase() ===
                                          "canceled" ||
                                        li?.appointmentStatus?.toLowerCase() ===
                                          "cancelled" ||
                                        li?.appointmentStatus?.toLowerCase() ===
                                          "noshow"
                                          ? "card-con-red-back"
                                          : li?.appointmentStatus?.toLowerCase() ===
                                              "completed"
                                            ? "card-con-green-back"
                                            : li?.appointmentStatus?.toLowerCase() ===
                                                  "consultation" ||
                                                li?.appointmentStatus?.toLowerCase() ===
                                                  "finalize"
                                              ? "card-top-yellow-color"
                                              : "card-top-orange-color"
                                      }`}
                                    >
                                      {getTypeByAppointmentType(
                                        li?.appointmentStatus,
                                      )}
                                    </Typography>
                                  ) : hdr === "action" ? (
                                    <div className="flex1-end pos-rel new-cal-tbl-action-menu">
                                      {li?.appointmentType === "Physical" &&
                                      (li?.appointmentStatus?.toLowerCase() ===
                                        "cancelled" ||
                                        li?.appointmentStatus?.toLowerCase() ===
                                          "canceled" ||
                                        li?.appointmentStatus?.toLowerCase() ===
                                          "noshow")
                                        ? null
                                        : li?.appointmentType !== "Virtual" &&
                                          li?.appointmentStatus?.toLowerCase() !==
                                            "pending" && (
                                            <MoreVertIcon
                                              className="new-cal-apnt-menu-trigger"
                                              sx={{
                                                height: 20,
                                                cursor: "pointer",
                                                transition: "color 0.3s ease",
                                                ":hover": {
                                                  color: "#1976d2",
                                                },
                                              }}
                                              aria-controls={
                                                anchorEls?.[li?.appointmentId]
                                                  ? "account-menu"
                                                  : undefined
                                              }
                                              aria-haspopup="true"
                                              aria-expanded={
                                                anchorEls?.[li?.appointmentId]
                                                  ? "true"
                                                  : undefined
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                anchorEls?.[li?.appointmentId]
                                                  ? handleClose(
                                                      li?.appointmentId,
                                                    )
                                                  : handleClick(
                                                      li?.appointmentId,
                                                      e,
                                                      li,
                                                      "list",
                                                    );
                                              }}
                                            />
                                          )}
                                    </div>
                                  ) : (
                                    <Typography className="font-medium fs14 black">
                                      {li?.[hdr]}
                                    </Typography>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          </>
                        );
                      })}
                    </TableBody>
                    {Math.ceil(appointmentsByFilter?.totalRecords / itemSize) >
                    1 ? (
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={12}>
                            <div className="flex-end">
                              <Pagination
                                count={Math.ceil(
                                  appointmentsByFilter?.totalRecords / itemSize,
                                )}
                                variant="outlined"
                                color="primary"
                                page={page}
                                onChange={handleChangePage}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    ) : null}
                  </Table>
                </TableContainer>
              </Container>
            </Grid>
          </Grid>
        </>
      ) : null}

      {updateWeightVsble ? (
        <UpdatePetWeight
          modVsble={updateWeightVsble}
          onCloseMod={onCloseUpdateWeight}
          details={modValues}
        />
      ) : null}

      {selectedHistory ? (
        <MedicalHistoryView
          selectedHistory={selectedHistory}
          petData={petData}
          onUploadComplete={(e, apntId) => handleUploadComplete(e, apntId)}
        />
      ) : null}

      {flowModVsble || reSchReAssModVsble ? (
        <BookAnAppointment
          modalVisible={flowModVsble || reSchReAssModVsble}
          setBookModVisible={onCloseBookFlowMod}
          selectedPet={{
            petId: slctedApnt?.petId,
            petName: slctedApnt?.petName,
            petImage: slctedApnt?.petImage,
            gender: slctedApnt?.gender,
            breed: slctedApnt?.breed,
            dob: slctedApnt?.dob,
            userName: slctedApnt?.userName,
            color: slctedApnt?.color,
            weight: slctedApnt?.weight,
            userMobile: slctedApnt?.userMobile,
            mobile: slctedApnt?.mobile,
          }}
          prevAppointmentId={slctedApnt?.appointmentId}
          appointment={slctedApnt}
          onBookModalCompleted={() => {
            onCallRightApi();
            // handleApiCall();
            onCloseReSchReAss();
          }}
          isEditAppointment={reSchReAssModVsble}
          rescheduleOrReassignModVsble={reSchReAssModVsble}
          isReassign={slctedApnt?.reAssign ?? false}
        />
      ) : null}

      {apntBookModVsble ? (
        <VetBookAnAppointment
          modalVisible={apntBookModVsble}
          setBookModVisible={onCloseBookMod}
          handleCloseMod={handleBookModClose}
          selectedVet={{
            doctorId: modValues?.vet?.doctorId ?? modValues?.vet?.value,
            name: modValues?.vet?.name,
            date: modValues?.date,
            startTime: modValues?.startTime,
            facility:
              modValues?.facility?.value ?? modValues?.facility?.id ?? null,
          }}
          addExtraOption
          slotSize={calFilterValues?.slotSize ?? null}
        />
      ) : null}

      {unavailEditModVsble ? (
        <VetBookAnAppointment
          modalVisible={unavailEditModVsble}
          setBookModVisible={onCloseUnavailEditMod}
          handleCloseMod={handleUnavailEditModClose}
          selectedVet={unavailEditData?.vet}
          defaultType="internalUse"
          initialInternalValues={unavailEditData?.internalValues}
          editUnavailabilityId={unavailEditData?.holliId}
          slotSize={getUnavailabilitySlotSize(
            unavailEditData?.vet?.startTime,
            unavailEditData?.vet?.endTime,
            calFilterValues?.slotSize ?? 15,
          )}
        />
      ) : null}

      <CustomModal
        open={unavailDeleteModVsble}
        onClose={onCloseDeleteUnavailabilityMod}
        header="Delete Unavailability"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete this unavailability slot?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onConfirmDeleteUnavailability}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton
                text="No"
                onClick={onCloseDeleteUnavailabilityMod}
                smallBtn
              />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={canModVsble}
        onClose={onCloseCancelMod}
        header="Cancel"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to cancel the appointment?
        </Typography>
        <div className="ml10 mt15">
          <CustomTextField
            label="Reason for cancellation"
            placeholder="Enter reason for cancellation (optional)"
            multiline
            rows={4}
            value={cancelReason}
            handleChange={(e) => setCancelReason(e.target.value)}
            fullWidth
          />
        </div>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton text="Yes" redBtn onClick={conCancel} smallBtn />
            </div>
            <div className="ml10">
              <CustomButton
                text="No"
                onClick={() => {
                  onCloseCancelMod();
                  setCancelReason("");
                }}
                smallBtn
              />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={noShowModVsble}
        onClose={onCloseNoShowMod}
        header="No Show"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to mark the appointment as no-show?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onConfirmNoShow}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={onCloseNoShowMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <AdmitPatient
        modalVisible={admitModVsble}
        onCloseMod={onCloseAdmitMod}
        modalData={slctedApnt}
      />

      <CustomModal
        open={calFilterValues?.visible}
        onClose={() => onChangeCalValue("visible", false)}
        header="Filter"
        rightModal
        modalWidth={25}
      >
        <div className="scroll-80vh ph10">
          <div>
            <div className="flex-row-ali-cen card-box">
              <div className="txt-semi-bold fs12 black31">DISPLAY SETTINGS</div>
              <div
                className="flex1-end"
                onClick={() =>
                  onChangeTempValue(
                    "settingVisible",
                    !tempFilterValues?.settingVisible,
                  )
                }
              >
                {tempFilterValues?.settingVisible ? (
                  <ArrowDropDownIcon />
                ) : (
                  <ArrowRightIcon />
                )}
              </div>
            </div>
            {tempFilterValues?.settingVisible ? (
              <div className="card-box-wo-back">
                <div>
                  <CustomCheckbox
                    label="Show stats cards"
                    checked={tempFilterValues?.showStats}
                    onChange={() =>
                      onChangeTempValue(
                        "showStats",
                        !tempFilterValues?.showStats,
                      )
                    }
                  />
                </div>
                <div className="mtMin15">
                  <CustomCheckbox
                    label="Show availability"
                    checked={tempFilterValues?.showAvailability}
                    onChange={() =>
                      onChangeTempValue(
                        "showAvailability",
                        !tempFilterValues?.showAvailability,
                      )
                    }
                  />
                </div>
                {/* <div className="txt-regular fs11 black31 mtMin10">
                  Availability: day view only; shows staff with open slots.
                </div> */}
                <div>
                  <div className="font-medium fs13">Slot Size</div>
                  <div className="flex-row-between-align-center">
                    <div className="w30Per">
                      <div
                        className={`cursor fs11 font-medium card-box-wo-back flex-center ${tempFilterValues?.slotSize === 60 ? "blu-back white-color" : ""}`}
                        onClick={() => onChangeTempValue("slotSize", 60)}
                      >
                        60 min
                      </div>
                    </div>
                    <div className="w30Per">
                      <div
                        className={`cursor fs11 font-medium card-box-wo-back flex-center ${tempFilterValues?.slotSize === 30 ? "blu-back white-color" : ""}`}
                        onClick={() => onChangeTempValue("slotSize", 30)}
                      >
                        30 min
                      </div>
                    </div>
                    <div className="w30Per">
                      <div
                        className={`cursor fs11 font-medium card-box-wo-back flex-center ${tempFilterValues?.slotSize === 15 ? "blu-back white-color" : ""}`}
                        onClick={() => onChangeTempValue("slotSize", 15)}
                      >
                        15 min
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="card-box-wo-back mt15">
              <div className="txt-semi-bold fs12 black31">APPOINTMENT TYPE</div>
              <div>
                <CustomCheckbox
                  label="Physical"
                  checked={tempFilterValues?.showPhysical}
                  onChange={() =>
                    onChangeTempValue(
                      "showPhysical",
                      !tempFilterValues?.showPhysical,
                    )
                  }
                />
              </div>
              <div className="mtMin15">
                <CustomCheckbox
                  label="Virtual"
                  checked={tempFilterValues?.showVirtual}
                  onChange={() =>
                    onChangeTempValue(
                      "showVirtual",
                      !tempFilterValues?.showVirtual,
                    )
                  }
                />
              </div>
              <div className="mtMin15">
                <CustomCheckbox
                  label="Home Visit"
                  checked={tempFilterValues?.showHomeVisit}
                  onChange={() =>
                    onChangeTempValue(
                      "showHomeVisit",
                      !tempFilterValues?.showHomeVisit,
                    )
                  }
                />
              </div>
            </div>
            <div className="card-box-wo-back mt15">
              <div className="txt-semi-bold fs12 black31">SERVICE TYPE</div>
              <div className="mv5">
                <CustomSelect
                  list={getListWithAll(serviceTypes)}
                  value={tempFilterValues?.serviceType ?? []}
                  name="serviceType"
                  handleChange={(e) =>
                    onChangeTempValue("serviceType", e?.target?.value)
                  }
                  multiSelectSearchCheck
                />
              </div>
            </div>
            {perspective === PERSPECTIVE_VET ? (
              <>
                <div className="card-box-wo-back mt15">
                  <div className="txt-semi-bold fs12 black31">ROLES</div>
                  <div className="mv5">
                    <CustomSelect
                      list={[
                        { name: "Vets", value: "Vets" },
                        { name: "Groomers", value: "Groomers" },
                        {
                          name: "Lab/Radiology Tech",
                          value: "Lab/Radiology Tech",
                        },
                      ]}
                      value={tempFilterValues?.roles}
                      name="roles"
                      handleChange={(e) =>
                        onChangeTempValue("roles", e?.target?.value)
                      }
                      select
                    />
                  </div>
                </div>
                <div className="card-box-wo-back mt15">
                  <div className="txt-semi-bold fs12 black31">VETS/STAFFS</div>
                  <div className="mv5">
                    <CustomSelect
                      list={getListWithAll(getVetListNew(false, true))}
                      value={tempFilterValues?.vets ?? []}
                      name="vets"
                      handleChange={(e) =>
                        onChangeTempValue("vets", e?.target?.value)
                      }
                      multiSelectSearchCheck
                    />
                  </div>
                </div>
              </>
            ) : null}
            {perspective === PERSPECTIVE_FACILITY ? (
              <div className="card-box-wo-back mt15">
                <div className="txt-semi-bold fs12 black31">FACILITY TYPE</div>
                <div className="mv5">
                  <CustomSelect
                    list={getListWithAll(facilityTypes)}
                    value={tempFilterValues?.facilityTypes ?? []}
                    name="facilityTypes"
                    handleChange={(e) =>
                      onChangeTempValue("facilityTypes", e?.target?.value)
                    }
                    multiSelectSearchCheck
                  />
                </div>
              </div>
            ) : null}
            <div className="card-box-wo-back mt15">
              <div className="txt-semi-bold fs12 black31">
                APPOINTMENT STATUS
              </div>
              <div className="mv5">
                <CustomSelect
                  list={appointmentStatusList}
                  value={tempFilterValues?.appointmentStatus ?? []}
                  name="appointmentStatus"
                  handleChange={(e) =>
                    onChangeTempValue("appointmentStatus", e?.target?.value)
                  }
                  multiSelectSearchCheck
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex-row flex-center mt15">
          <div className="w40Per">
            <CustomButton
              text="Reset All"
              smallBtn
              whiteBtn
              onClick={() => onMainFilterApply(true)}
            />
          </div>
          <div className="w40Per ml15">
            <CustomButton
              text="Apply Filters"
              smallBtn
              onClick={() => onMainFilterApply(false)}
            />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => (
                      <div className="mt20">
                        {["Type", "Service Type"]?.includes(li) ? (
                          <CustomSelect
                            list={
                              li === "Type"
                                ? bookingTypes
                                : li === "Service Type"
                                  ? serviceTypes
                                  : []
                            }
                            value={filterValues?.values?.[li] ?? []}
                            name={li}
                            handleChange={onFilterValuesChange}
                            label={li}
                            multiSelectSearchCheck={li !== "Type"}
                            select={li === "Type"}
                          />
                        ) : li === "Appt Date" ? (
                          <div className="flex-row-between-align-center">
                            <div className="w45Per">
                              <CustomTextField
                                placeholder="Start Date"
                                fullWidth
                                name="Start Date"
                                handleChange={onFilterValuesChange}
                                value={
                                  filterValues?.values?.["Start Date"] ?? ""
                                }
                                label="Start Date"
                                mobileDate
                              />
                            </div>
                            <div className="w45Per">
                              <CustomTextField
                                placeholder="End Date"
                                fullWidth
                                name="End Date"
                                handleChange={onFilterValuesChange}
                                value={filterValues?.values?.["End Date"] ?? ""}
                                label="End Date"
                                mobileDate
                              />
                            </div>
                          </div>
                        ) : li === "Appt Time" ? (
                          <div className="flex-row-between-align-center">
                            <div className="w45Per">
                              <CustomTextField
                                placeholder="Start Time"
                                fullWidth
                                name="Start Time"
                                handleChange={onFilterValuesChange}
                                value={
                                  filterValues?.values?.["Start Time"] ?? ""
                                }
                                label="Start Time"
                                mobileTime
                                is12HourFomat={false}
                              />
                            </div>
                            <div className="w45Per">
                              <CustomTextField
                                placeholder="End Time"
                                fullWidth
                                name="End Time"
                                handleChange={onFilterValuesChange}
                                value={filterValues?.values?.["End Time"] ?? ""}
                                label="End Time"
                                mobileTime
                                is12HourFomat={false}
                              />
                            </div>
                          </div>
                        ) : (
                          <CustomTextField
                            placeholder={li}
                            fullWidth
                            name={li}
                            handleChange={onFilterValuesChange}
                            value={filterValues?.values?.[li] ?? ""}
                            label={li}
                            suggetions
                            options={getSuggestionList()}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>

      <CustomModal
        open={assignVetModVisible}
        onClose={onCloseAssignVetMod}
        header={
          clinicDetails?.id === slctedApnt?.doctorId &&
          !checkIsVet(profile?.id) &&
          !slctedApnt?.facilityId
            ? "Select Vet and Facility"
            : !slctedApnt?.facilityId
              ? "Select Facility"
              : "Select Vet"
        }
        modal
        modalWidth={30}
      >
        <div className="mh10">
          {clinicDetails?.id === slctedApnt?.doctorId &&
          !checkIsVet(profile?.id) ? (
            <div>
              <CustomSelect
                list={getVetList()}
                handleChange={(e, value) =>
                  onAssignVetChange("vet", value, "error")
                }
                name="vet"
                label="Vet Name *"
                search
                value={assignVetvalues?.vet}
                error={!!assignVetvalues?.error}
                helperText={
                  assignVetvalues?.error ? "Vet Name is required field" : ""
                }
              />
            </div>
          ) : null}
          {!slctedApnt?.facilityId ? (
            <div
              className={
                clinicDetails?.id === slctedApnt?.doctorId &&
                !checkIsVet(slctedApnt?.doctorId) &&
                !checkIsVet(profile?.id)
                  ? "mt15"
                  : ""
              }
            >
              <CustomSelect
                list={getFacilitiesByType(slctedApnt?.serviceType)}
                handleChange={(e, value) =>
                  onAssignVetChange("facility", value, "facilityErr")
                }
                name="facility"
                label="Facility *"
                search
                value={assignVetvalues?.facility}
                error={!!assignVetvalues?.facilityErr}
                helperText={
                  assignVetvalues?.facilityErr
                    ? "Facility is required field"
                    : ""
                }
              />
            </div>
          ) : null}
          <div className="flex-center mv20">
            <div>
              <CustomButton text="Save" onClick={onAssignVet} smallBtn />
            </div>
          </div>
        </div>
      </CustomModal>

      {renderMoreApntsPopup()}
      {renderApntMenuPortal()}
      {renderUnavailMenuPortal()}
    </>
  );
};

export default NewCalendar;
